/* FG Customiser (new, image-based) — Lit root component.
 *
 * Renders the photographic layered image + per-component colour swatches (with real availability)
 * + price, and adds the configuration to cart as an individual-line-item WICK bundle (real
 * components => real inventory; the frankgreen-bundles cart-transform merges + discounts). Shadow
 * DOM keeps styles encapsulated from the theme. Data comes from the `product.customiser` JSON
 * contract via `?view=customiser` (see PROJECT.md — that data template must exist on this theme).
 *
 * NOTE (new image customiser = `fg-customiser`, with an S) is distinct from the shelved 3D
 * `fg-customizer` (with a Z) that also lives in this theme.
 *
 * Next increments: monogram (Format B) UI + line, add-ons, gift-set groups, multi-product
 * components / inner_parts, smarter image-layer ordering, custom-image (S3) capture.
 */
import { LitElement, html, css } from 'lit';

/* ─────────────────────────────────────────────────────────────────────────────
 * FEATURE FLAG — swatch colour ordering + sold-out hiding (bottles only).
 *
 * Per client (2026-07-22): the customer-facing colour-order dropdown + "Hide sold
 * out" checkbox are REMOVED (SHOW_CONTROLS:false). The behaviour they drove now
 * ships locked-in: swatches sort Light→Dark with any Chrome finishes pinned first,
 * and sold-out colours are always hidden. To turn the whole feature OFF, set
 * ENABLED to false: swatches revert to native product order with nothing hidden.
 * ──────────────────────────────────────────────────────────────────────────── */
const SWATCH_CONTROLS = {
  ENABLED: true,                    // ← master on/off switch (reorder + hide)
  SHOW_CONTROLS: false,             // customer-facing dropdown + checkbox (removed per client)
  DEFAULT_ORDER: 'light',           // locked order: 'light' = Light→Dark (Chromes pinned first)
  DEFAULT_HIDE_SOLD_OUT: true,      // sold-out colours always hidden
};

/* ── WICK cart module (inlined) ─────────────────────────────────────────────
 * Previously imported from ./fg-customiser-cart.js, but a relative module import
 * has no ?v= cache-buster, so the browser/CDN kept serving a stale copy after
 * every deploy. Inlining it here means the ONLY asset is this file, which loads
 * via asset_url (?v=…) and is always fresh. Adds each selected component variant
 * as its own cart line sharing one _wick_bundle_id under the customisable PARENT
 * variant; the frankgreen-bundles cart-transform merges + discounts them.
 * ─────────────────────────────────────────────────────────────────────────── */
function cartRootOf(root) {
  try { var r = root || (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; return r.charAt(r.length - 1) === '/' ? r : r + '/'; }
  catch (e) { return '/'; }
}
const gid = (id) => 'gid://shopify/ProductVariant/' + id;

// Inner parts of a product (composite lids: hull/strap/straw or hull/button), or null.
function innerProducts(p) {
  return (p && p.inner_parts && Array.isArray(p.inner_parts.products) && p.inner_parts.products.length) ? p.inner_parts.products : null;
}
// The selected variant of an inner part, from sel[c.name].inner[ip.id] (fallback: first available).
function innerVariant(ip, sel, cName) {
  const chosen = ((sel[cName] || {}).inner || {})[ip.id];
  const vs = ip.variants || [];
  return vs.find((v) => v.id === chosen) || vs.find((v) => v.available) || vs[0] || null;
}
function resolve(data, sel) {
  const parts = [];
  (data.components || []).forEach((c) => {
    const s = sel[c.name]; if (!s) return;
    // Assembly (standalone lid): one cart line per selected part (hull/strap/straw).
    if (s.assembly) {
      (c.products || []).forEach((p) => {
        const v = (p.variants || []).find((x) => x.id === (s.parts || {})[p.id]);
        if (v) parts.push({ id: v.id, role: 'part', component: c.name + ':' + p.id, surcharge: Number(v.surcharge_price || 0) });
      });
      return;
    }
    const p = (c.products || []).find((p) => p.id === s.productId) || (c.products || [])[0];
    if (!p) return;
    const ips = innerProducts(p);
    if (ips) {
      // Composite lid: each inner part (hull/strap/straw or hull/button) is its own cart line — this is
      // what captures the chosen sub-part colours (the wrapper product has a single colourless variant).
      ips.forEach((ip) => {
        const v = innerVariant(ip, sel, c.name);
        if (v) parts.push({ id: v.id, role: 'part', component: c.name + ':' + ip.id, surcharge: Number(v.surcharge_price || 0) });
      });
    } else {
      const v = (p.variants || []).find((v) => v.id === s.variantId) || null;
      if (v) parts.push({ id: v.id, role: 'part', component: c.name, surcharge: Number(v.surcharge_price || 0) });
    }
  });
  return parts;
}

// Selected Extras (add_ons) -> cart lines. `extras` = { [addOnId]: { variantId, inner:{[ipId]:variantId} } }
// (a bare id is also accepted for back-compat). A COMPOSITE add-on (e.g. an extra lid with hull/strap/straw
// or hull/button inner parts) contributes one cart line per chosen inner part — mirroring how the main lid
// resolves — with the add-on's price attributed once (the inner parts' own prices are covered by it).
function resolveExtras(data, extras) {
  const out = [];
  (data.add_ons || []).forEach((a) => {
    const sel = (extras || {})[a.id]; if (!sel) return;
    const price = Number(a.price || 0);
    const ips = innerProducts(a);
    if (ips) {
      const inner = (sel && typeof sel === 'object' && sel.inner) ? sel.inner : {};
      let first = true;
      ips.forEach((ip) => {
        const v = (ip.variants || []).find((x) => x.id === inner[ip.id]); if (!v) return;
        out.push({ id: v.id, priceCents: first ? price : 0, title: a.title, variantTitle: (v.colour_swatch && v.colour_swatch.name) || v.title });
        first = false;
      });
      if (first) { const wv = (a.variants || [])[0]; if (wv) out.push({ id: wv.id, priceCents: price, title: a.title, variantTitle: wv.title }); }
    } else {
      const vid = (sel && typeof sel === 'object') ? sel.variantId : sel;
      const v = (a.variants || []).find((x) => x.id === vid) || (a.variants || [])[0];
      if (v) out.push({ id: v.id, priceCents: price, title: a.title, variantTitle: v.title });
    }
  });
  return out;
}

// Walk any customiser dataset and record every variant's live availability, keyed by variant id. The
// tree nests components -> products -> inner_parts.products -> variants (plus add_ons / gift groups),
// so recurse on every `variants` array found rather than hard-coding the shape.
function collectVariantAvail(node, map) {
  if (!node || typeof node !== 'object') return;
  if (Array.isArray(node)) { for (const n of node) collectVariantAvail(n, map); return; }
  if (Array.isArray(node.variants)) {
    for (const v of node.variants) {
      if (v && v.id != null) map.set(String(v.id), { available: !!v.available, name: (v.colour_swatch && v.colour_swatch.name) || v.title || '' });
    }
  }
  for (const k in node) { const val = node[k]; if (val && typeof val === 'object') collectVariantAvail(val, map); }
}
// Apply a fresh availability map back onto a dataset in place, so the UI re-renders with live stock.
function applyVariantAvail(node, map) {
  if (!node || typeof node !== 'object') return;
  if (Array.isArray(node)) { for (const n of node) applyVariantAvail(n, map); return; }
  if (Array.isArray(node.variants)) {
    for (const v of node.variants) { const r = v && v.id != null && map.get(String(v.id)); if (r) v.available = r.available; }
  }
  for (const k in node) { const val = node[k]; if (val && typeof val === 'object') applyVariantAvail(val, map); }
}

// id -> variant object for every variant anywhere in a dataset, so a resolved cart line (which carries
// only an id) can be traced back to its stock + colour name without re-walking the tree on every render.
// Cached per dataset: applyVariantAvail mutates the SAME variant objects in place, so a cached index keeps
// reporting live availability after a re-check.
const VARIANT_INDEX = new WeakMap();
function indexVariants(node, map) {
  if (!node || typeof node !== 'object') return;
  if (Array.isArray(node)) { for (const n of node) indexVariants(n, map); return; }
  if (Array.isArray(node.variants)) { for (const v of node.variants) if (v && v.id != null) map.set(String(v.id), v); }
  for (const k in node) { const val = node[k]; if (val && typeof val === 'object') indexVariants(val, map); }
}
function variantIndex(data) {
  let m = VARIANT_INDEX.get(data);
  if (!m) { m = new Map(); indexVariants(data, m); VARIANT_INDEX.set(data, m); }
  return m;
}

// Monogram lines. `mono` = { [componentName]: { text, placement, emojis:[code], backText, backEmojis:[code] } }.
// Each active side (front/back) adds its own monogramming_product variant + human-readable properties.
function monoProps(m, text, placement, emojiCodes, isBack) {
  const p = {};
  p[isBack ? 'Monogram (back)' : 'Monogram'] = text || '';
  if (placement) p[isBack ? 'Back placement' : 'Placement'] = placement;
  if (emojiCodes && emojiCodes.length) {
    const names = emojiCodes.map((code) => { const e = (m.emojis || []).find((x) => x.code === code); return e ? e.name : code; });
    p[isBack ? 'Back emoji' : 'Emoji'] = names.join(', ');
  }
  return p;
}
// The template emits monogramming on each PRODUCT (inside component.products[]), not the component,
// so resolve it from the component's selected product (mirrors the component's `_productFor`).
function productForSel(c, sel) {
  const s = (sel || {})[c.name];
  return (c.products || []).find((p) => p.id === s?.productId) || (c.products || [])[0] || null;
}
function resolveMono(data, sel, mono) {
  const out = [];
  (data.components || []).forEach((c) => {
    const st = (mono || {})[c.name]; if (!st) return;
    const src = productForSel(c, sel) || {};
    const f = src.monogramming, b = src.monogramming_back;
    if (f && f.product_id && (st.text || (st.emojis && st.emojis.length))) {
      out.push({ id: f.variant_id, priceCents: Number(f.price || 0), props: monoProps(f, st.text, st.placement || f.placement, st.emojis, false) });
    }
    if (b && b.product_id && (st.backText || (st.backEmojis && st.backEmojis.length))) {
      out.push({ id: b.variant_id, priceCents: Number(b.price || 0), props: monoProps(b, st.backText, b.placement, st.backEmojis, true) });
    }
  });
  return out;
}

// Monogram text that must never reach the cart, per component: `banned` (matches the store's
// banned_words list) or `too_long` (over the product's character_limit — possible when a product
// group swaps to a product with a lower limit, since the typed text is kept per component).
// Mirrors resolveMono exactly, so only text that WOULD be added is judged. The warning under the
// input used to be the only consequence, and banned monograms were reaching the 3PL (client,
// 2026-09-29) — Add to cart is now gated on this being empty.
function monoIssues(data, sel, mono, isBanned) {
  const out = [];
  (data.components || []).forEach((c) => {
    const st = (mono || {})[c.name]; if (!st) return;
    const src = productForSel(c, sel) || {};
    [[src.monogramming, st.text], [src.monogramming_back, st.backText]].forEach(([m, text]) => {
      if (!m || !m.product_id || !text) return;
      if (isBanned(text)) out.push({ name: c.name, reason: 'banned' });
      else if (m.character_limit && text.length > Number(m.character_limit)) out.push({ name: c.name, reason: 'too_long' });
    });
  });
  return out;
}

function bundleCents(data, sel, mono, extras) {
  let c = Number(data.product && data.product.price) || 0;
  resolve(data, sel).forEach((p) => { c += Math.round((p.surcharge || 0) * 100); });
  resolveMono(data, sel, mono).forEach((m) => { c += Math.round(m.priceCents || 0); });
  resolveExtras(data, extras).forEach((e) => { c += Math.round(e.priceCents || 0); });
  return c;
}

// The variant the WICK bundle is parented to — what the cart line shows as the product + colour.
//
// A multi-part bottle is parented to a separate, colourless `customisable-…` product, so the JSON's
// cart_parent_variant_id is right. But a SELF-PARENTED product (one component whose product IS the PDP
// product — Big Chill, To Go Bowls, monogram-only chromes) has no such stand-in: its cart parent is
// itself, and the JSON is rendered from `/products/<handle>?view=customiser` with no `variant=`, so
// cart_parent_variant_id is always Liquid's first available variant. Every add landed under that one
// colour no matter what the shopper picked (BUG: NZ Big Chill always showed "Color: Mint Gelato").
// When the parent is this product, follow the shopper's own selection instead.
function parentVariantFor(data, parts) {
  const p = data.product || {};
  const fallback = p.cart_parent_variant_id || p.variant_id;
  const selfParented = !p.cart_parent_handle || p.cart_parent_handle === p.handle;
  if (!selfParented) return fallback;
  const own = new Set((p.variants || []).map((v) => v.id));
  const chosen = (parts || []).find((x) => own.has(x.id));
  return chosen ? chosen.id : fallback;
}

function buildItems(data, sel, bundleId, bundleNum, mono, extras) {
  const parts = resolve(data, sel);
  if (!parts.length) return { items: [], count: 0 };
  const monos = resolveMono(data, sel, mono);
  const exs = resolveExtras(data, extras);
  const total = parts.length + monos.length + exs.length;
  // Parent = the size's customisable product (cart_parent_product), NOT the combo variant — else the
  // wick transform merges the custom parts under the combo's preselected-bundle variant and the cart
  // collapses to that combo colour. Falls back to the combo variant if no cart_parent is resolved.
  const parentVid = parentVariantFor(data, parts);
  const name = data.product.cart_parent_title || data.product.title || 'Custom bottle';
  const price = (bundleCents(data, sel, mono, extras) / 100).toFixed(2);
  const count = String(total);
  const wick = (role, extra) => Object.assign({
    _wick_bundle_id: bundleId,
    _wick_bundle_name: name,
    _wick_parent_variant_id: gid(parentVid),
    _wick_bundle_price: price,
    _wick_bundle_item_count: count,
    _wick_bundle_role: role,
    'Bundle #': String(bundleNum),
  }, extra || {});
  const items = [];
  parts.forEach((p) => items.push({ id: p.id, quantity: 1, properties: wick(p.role) }));
  monos.forEach((m) => items.push({ id: m.id, quantity: 1, properties: wick('monogram', m.props) }));
  exs.forEach((e) => items.push({ id: e.id, quantity: 1, properties: wick('add_on') }));
  return { items, count: total, parentVid, name };
}

// GIFT SET: flatten every bundle item (fixed products + chosen group products) into ONE WICK bundle,
// parented to the gift set product's variant at a fixed total price. Each item's parts/monogram lines
// carry an "Item" property so the cart/fulfilment can tell which product in the set they belong to.
// Mirrors AU: the set has its own price; items contribute only surcharges + monogram + add-ons.
function buildGiftItems(giftSet, items, bundleId, bundleNum, totalCents, setExtras) {
  const parentVid = giftSet.variant_id;
  const name = giftSet.title || 'Gift set';
  const price = (totalCents / 100).toFixed(2);
  // Two members of a set can legitimately share a variant — both bottles in Double
  // Hydration take the SAME straw (31077950128201). Shopify MERGES cart lines that have
  // the same variant AND identical properties, so those two straws collapsed into one
  // line of quantity 2. The cart then held fewer lines than `_wick_bundle_item_count`
  // claimed and the transform refused to merge, leaving every part loose at full price.
  //
  // `Item` alone cannot prevent that: it comes from `data.product.title`, which resolves
  // to the shared cart-parent title ("Ceramic Reusable Bottle") for both members, so the
  // properties were byte-identical. The old customiser avoided this because its
  // `_customiser-part` value embedded the member index.
  //
  // So stamp an explicit per-member index on every line. Underscore-prefixed, so it stays
  // out of the cart display, and deliberately NOT `_wick_*` so it can't be mistaken for
  // an input to the transform. This makes two members' lines distinct even when they
  // share a variant and a title.
  const lines = [];

  // UK + AU: every gift set ships with a packaging SKU. It is not shopper-selectable, so it is
  // added here rather than offered as an extra.
  //
  // Pushed FIRST, which reads backwards until you look at a real checkout: CHECKOUT RENDERS A
  // MERGED BUNDLE'S COMPONENTS IN REVERSE OF THE ORDER THEY WERE POSTED IN. Verified 2026-08-25
  // against Double Hydration on AU — all 9 lines matched the flip exactly, box included.
  // Appending it last therefore put "Double Hydration Gift Set Box" at the TOP of the shopper's
  // item list, where it reads like the headline product. First here = last there.
  //
  // Still pushed BEFORE `count` is taken: the wick transform refuses to merge a bundle unless
  // the cart holds exactly `_wick_bundle_item_count` lines carrying the id — the same mismatch
  // that left Double Hydration's components loose at full price. The packaging product is
  // zero-priced and inventory-untracked, so it can neither alter the set price nor be blocked
  // by stock.
  //
  // `_fg_packaging` is underscore-prefixed to stay out of the cart display, and deliberately
  // not `_wick_*` so it can never be read as an input to the transform (same reasoning as
  // `_fg_gift_item`). The transform keys its own ordering off it — see orderPackagingFirst.
  if (giftSet.packagingVid) {
    lines.push({ id: giftSet.packagingVid, role: 'add_on', props: { _fg_packaging: '1' } });
  }
  const rawTitles = items.map((item, idx) =>
    (item.data && item.data.product && item.data.product.title) || ('Item ' + (idx + 1)));
  // Keep clean titles when they're already unique; only disambiguate when they collide,
  // otherwise every set would start showing "(1)" suffixes it never had before.
  const titleCount = rawTitles.reduce((m, t) => (m[t] = (m[t] || 0) + 1, m), {});
  const titles = rawTitles.map((t, idx) => (titleCount[t] > 1 ? t + ' (' + (idx + 1) + ')' : t));

  items.forEach((item, idx) => {
    const itemTitle = titles[idx];
    const member = { Item: itemTitle, _fg_gift_item: String(idx) };
    resolve(item.data, item.sel).forEach((p) => lines.push({ id: p.id, role: 'part', props: Object.assign({}, member) }));
    resolveMono(item.data, item.sel, item.mono).forEach((m) => lines.push({ id: m.id, role: 'monogram', props: Object.assign({}, member, m.props) }));
    resolveExtras(item.data, item.extras).forEach((e) => lines.push({ id: e.id, role: 'add_on', props: Object.assign({}, member) }));
  });
  (setExtras || []).forEach((e) => lines.push({ id: e.id, role: 'add_on', props: {} }));

  const count = lines.length;
  const wick = (role, extra) => Object.assign({
    _wick_bundle_id: bundleId,
    _wick_bundle_name: name,
    _wick_parent_variant_id: gid(parentVid),
    _wick_bundle_price: price,
    _wick_bundle_item_count: String(count),
    _wick_bundle_role: role,
    'Bundle #': String(bundleNum),
  }, extra || {});
  const out = lines.map((l) => ({ id: l.id, quantity: 1, properties: wick(l.role, l.props) }));
  return { items: out, count, name, parentVid };
}

// Distinct WICK bundle count in the cart -> the next Bundle # (so multiple customised adds stay separate).
async function scanBundleNum(root) {
  const cart = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => ({ items: [] }));
  const ids = {}; (cart.items || []).forEach((it) => { const b = it.properties && it.properties._wick_bundle_id; if (b) ids[b] = 1; });
  return Object.keys(ids).length + 1;
}

// POST prebuilt line items to the cart, stash the thumbnail + summary by bundle id, and refresh the cart UI.
async function submitItems(opts) {
  const { root, items, bundleId, name } = opts;
  const afterAdd = opts.afterAdd || 'drawer';
  const cartEl = (afterAdd !== 'cart') ? (document.querySelector('cart-notification') || document.querySelector('cart-drawer')) : null;
  const body = { items };
  if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
    body.sections = cartEl.getSectionsToRender().map((s) => s.id);
    body.sections_url = window.location.pathname;
  }
  const res = await fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
  const json = await res.json();
  if (!res.ok) throw new Error(json.description || json.message || 'add failed');

  // stash the custom render + build summary keyed by bundle id so fg-cart-image.js can swap the cart
  // line image + add the summary by matching _wick_bundle_id (same key scheme as the 3D build).
  try {
    if (opts.thumbnail) localStorage.setItem('fg_cart_thumb_b_' + bundleId, opts.thumbnail);
    if (opts.summary) localStorage.setItem('fg_cart_sum_b_' + bundleId, JSON.stringify(opts.summary));
    localStorage.setItem('fg_cart_src_b_' + bundleId, window.location.pathname || '');
  } catch (e) {}

  console.log('[FG] added bundle', bundleId, items.length, 'lines @', name);

  if (afterAdd === 'cart') { window.location.href = root + 'cart'; return json; }
  if (cartEl && typeof cartEl.renderContents === 'function') { cartEl.renderContents(json); }
  else { refreshCart(root, afterAdd, json); }
  return json;
}

async function addToCart(opts) {
  const { data, sel, mono, extras } = opts;
  const root = cartRootOf(opts.cartRoot);
  const afterAdd = opts.afterAdd || 'drawer';
  const bundleNum = await scanBundleNum(root);
  const bundleId = 'wick_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  const built = buildItems(data, sel, bundleId, bundleNum, mono, extras);
  if (!built.items.length) throw new Error('Nothing to add — no part variants resolved');
  return submitItems({ root, afterAdd, items: built.items, bundleId, name: built.name, thumbnail: opts.thumbnail, summary: opts.summary });
}

function refreshCart(root, afterAdd, resp) {
  if (afterAdd === 'cart') { window.location.href = root + 'cart'; return; }
  document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: {} }));
  if (resp && resp.sections) {
    Object.keys(resp.sections).forEach((nm) => {
      const html = resp.sections[nm]; if (!html) return;
      const el = document.getElementById(nm) || document.getElementById('shopify-section-' + nm) || document.querySelector('[id*="' + nm + '"]');
      if (el) { const tmp = document.createElement('div'); tmp.innerHTML = html; const inner = tmp.querySelector('.shopify-section'); el.innerHTML = inner ? inner.innerHTML : html; }
    });
  }
  if (afterAdd === 'stay') return;
  const d = document.querySelector('cart-drawer') || document.getElementById('CartDrawer') || document.querySelector('.cart-drawer,[id^="CartDrawer"]');
  if (d) { if (typeof d.open === 'function') d.open(); else { d.classList.add('active', 'open', 'is-open'); document.documentElement.classList.add('cart-open'); } return; }
  window.location.href = root + 'cart';
}

const money = (cents, code) => {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: code || 'AUD', currencyDisplay: 'narrowSymbol' }).format((cents || 0) / 100); }
  catch (e) { return '$' + ((cents || 0) / 100).toFixed(2); }
};

// Decode HTML entities in a string (e.g. `can&#39;t` -> `can't`, `&amp;` -> `&`). Shopify's locale editor
// stores apostrophes/quotes/ampersands as entities, and since we render these strings as Lit text nodes
// (not innerHTML) the raw entity would otherwise show literally. Guarded on `&` so the common case is a
// no-op; safe against XSS because textarea.value never executes markup and Lit re-escapes on output.
const decodeEntities = (s) => {
  if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
  const t = document.createElement('textarea');
  t.innerHTML = s;
  return t.value;
};

// Shopify emits "Translation missing: …" for absent locale keys — that string is truthy, so guard it
// so `|| fallback` works. (Port the Products.customiser.* locale strings into the theme to remove these.)
const tstr = (v, fb) => decodeEntities((typeof v === 'string' && v && !/^Translation missing/.test(v)) ? v : fb);

// --- swatch ordering (drives the "Style Your Bottle" colour-order dropdown) ---------------------------
// r,g,b (0-255) -> HSL, hue in degrees, s/l in 0..1.
function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  let h = 0; const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60; if (h < 0) h += 360;
  }
  return { h, s, l };
}
// A swatch's flat colour as HSL, or null for image/gradient/pattern swatches (no single colour to sort by).
function swatchHsl(sw) {
  if (!sw || sw.image) return null;
  const c = String(sw.color || '').trim();
  let m, r, g, b;
  if ((m = /^#?([0-9a-f]{6})$/i.exec(c))) { r = parseInt(m[1].slice(0, 2), 16); g = parseInt(m[1].slice(2, 4), 16); b = parseInt(m[1].slice(4, 6), 16); }
  else if ((m = /^#?([0-9a-f]{3})$/i.exec(c))) { r = parseInt(m[1][0] + m[1][0], 16); g = parseInt(m[1][1] + m[1][1], 16); b = parseInt(m[1][2] + m[1][2], 16); }
  else if ((m = /rgba?\(([^)]+)\)/i.exec(c))) { const p = m[1].split(',').map((x) => parseFloat(x)); r = p[0]; g = p[1]; b = p[2]; }
  else return null;
  return rgbToHsl(r, g, b);
}
// A "chrome" (metallic) finish, matched by swatch/variant name — pinned first regardless of order mode.
function isChromeVariant(v) {
  const sw = (v && v.colour_swatch) || {};
  return /chrome/i.test(String(sw.name || v && v.title || ''));
}
// Compare two wrapped variants {v,i,hsl,chrome} for the given order mode. Chrome finishes always sort first
// (in native order among themselves). Then: image/no-colour swatches sort last in native order; for hue
// modes near-grey neutrals are grouped at the end (by lightness). 'featured' is pink-first (hue descending
// → pink→purple→blue→green→…), 'rainbow' is ROYGBIV (hue ascending).
function compareVariants(a, b, mode) {
  if (a.chrome !== b.chrome) return a.chrome ? -1 : 1;
  if (a.chrome && b.chrome) return a.i - b.i;
  if (!a.hsl && !b.hsl) return a.i - b.i;
  if (!a.hsl) return 1;
  if (!b.hsl) return -1;
  if (mode === 'featured' || mode === 'rainbow') {
    const na = a.hsl.s < 0.12, nb = b.hsl.s < 0.12;
    if (na !== nb) return na ? 1 : -1;
    if (na && nb) return a.hsl.l - b.hsl.l;
    const d = a.hsl.h - b.hsl.h;
    return (mode === 'featured' ? -d : d) || (a.i - b.i);
  }
  if (mode === 'dark') return (a.hsl.l - b.hsl.l) || (a.i - b.i);
  if (mode === 'light') return (b.hsl.l - a.hsl.l) || (a.i - b.i);
  if (mode === 'vivid') return (b.hsl.s - a.hsl.s) || (a.i - b.i);
  return a.i - b.i;
}

/* Lid-type line icons, ported verbatim from the AU theme assets (grip_and_sip_lid.svg etc.),
 * keyed by the AU part class. Rendered as data-URI <img> so no extra lit directive import is needed. */
const LID_ICONS = {
  'grip-and-sip-lid': `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M23.0575 16.9271C20.3809 17.1685 17.6211 17.1685 14.9442 16.9271C14.7751 16.9115 14.6331 17.0342 14.6184 17.199C14.6036 17.3642 14.7252 17.5099 14.8903 17.5248C16.2467 17.6467 17.6297 17.7088 19.0008 17.7088C20.3723 17.7088 21.7551 17.6467 23.1114 17.5248C23.2762 17.5099 23.3981 17.3642 23.3833 17.199C23.3684 17.0342 23.2251 16.9115 23.0575 16.9271Z" fill="black"/><path d="M28.9211 21.0966V11.5421C28.9211 9.73648 27.8336 8.1398 26.1504 7.47417C25.6836 7.2896 25.1621 7.19995 24.5562 7.19995H13.4461C12.8402 7.19995 12.3187 7.2896 11.8521 7.47417C10.1687 8.13999 9.08105 9.73667 9.08105 11.5421V21.0966C9.08105 21.2407 9.14382 21.3683 9.24133 21.4595C9.14383 21.5508 9.08105 21.6783 9.08105 21.8224V30.6423C9.08105 31.3416 9.57578 31.9455 10.2574 32.0779C13.1189 32.6353 16.0607 32.9177 19.0012 32.9177C21.9414 32.9177 24.8836 32.6353 27.7453 32.0779C28.4266 31.9455 28.9211 31.3416 28.9211 30.6423V21.9048C28.9211 21.7342 28.8302 21.591 28.6997 21.5007C28.8302 21.4105 28.9211 21.2673 28.9211 21.0966ZM10.0811 11.5421C10.0811 10.1498 10.9205 8.91792 12.2197 8.40425C12.5674 8.26675 12.9684 8.19995 13.4461 8.19995H24.5562C25.034 8.19995 25.4352 8.26675 25.7824 8.40425C27.0816 8.91792 27.9211 10.1496 27.9211 11.5421V21.0966C27.9211 21.1254 27.9328 21.1502 27.9374 21.1776C26.0261 21.5319 24.0715 21.7691 22.1207 21.8924C21.3219 21.0923 20.2184 20.5966 19.0012 20.5966C17.7839 20.5966 16.6804 21.0924 15.8816 21.8924C13.9307 21.7692 11.9761 21.5319 10.0647 21.1776C10.0694 21.1502 10.0811 21.1253 10.0811 21.0966V11.5421ZM19.0012 21.5966C20.8816 21.5966 22.4117 23.1267 22.4117 25.0072C22.4117 26.888 20.8816 28.4181 19.0012 28.4181C17.1205 28.4181 15.5906 26.888 15.5906 25.0072C15.5906 23.1267 17.1205 21.5966 19.0012 21.5966ZM27.9211 30.6423C27.9211 30.8642 27.7668 31.0552 27.5539 31.0966C21.9562 32.1869 16.0461 32.1869 10.4484 31.0966C10.2355 31.0552 10.081 30.8642 10.081 30.6423V21.8224C10.081 21.81 10.0749 21.7997 10.074 21.7875C11.8285 22.1082 13.6174 22.331 15.4068 22.4601C14.8949 23.1804 14.5906 24.0581 14.5906 25.0072C14.5906 27.4392 16.5691 29.4181 19.0012 29.4181C21.4332 29.4181 23.4117 27.4392 23.4117 25.0072C23.4117 24.0581 23.1075 23.1804 22.5955 22.4601C24.3907 22.3305 26.1856 22.107 27.9454 21.7846C27.9353 21.8241 27.9211 21.8622 27.9211 21.9048L27.9211 30.6423Z" fill="black"/><path d="M13.3519 15.8704C13.6281 15.8704 13.8519 15.6465 13.8519 15.3704C13.8519 15.165 13.7277 14.9894 13.5507 14.9124C17.1594 14.6433 20.8415 14.6433 24.4509 14.9124C24.2739 14.9894 24.1496 15.165 24.1496 15.3704C24.1496 15.6465 24.3734 15.8704 24.6496 15.8704C25.4867 15.8704 26.1676 15.1895 26.1676 14.3526V13.1666C26.1676 10.8725 25.8414 10.2512 23.5461 10.2512H14.4557C12.1604 10.2512 11.834 10.8725 11.834 13.1666V14.3526C11.834 15.1895 12.5148 15.8704 13.3519 15.8704ZM14.4557 11.2512H23.5461C25.1066 11.2512 25.1676 11.2512 25.1676 13.1666V13.9655C21.0904 13.6254 16.9103 13.6254 12.834 13.9655V13.1666C12.834 11.2512 12.8949 11.2512 14.4557 11.2512Z" fill="black"/></svg>`,
  'flip-straw-lid-hull': `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M37.6674 12.6396C37.6807 12.3666 37.4678 12.1345 37.192 12.121C36.9185 12.1063 36.6818 12.3186 36.6682 12.5917C36.3816 18.4831 35.6875 21.578 30.7346 23.8354C30.734 23.8293 30.7329 23.8233 30.7322 23.8173L30.7103 23.6073C30.6818 23.335 30.4299 23.135 30.1619 23.1656C29.8869 23.1935 29.6873 23.4367 29.7158 23.7086L29.7377 23.9186C29.7479 24.0173 29.7638 24.1145 29.7763 24.2127C29.692 24.2382 29.6061 24.2645 29.5287 24.2864C29.2181 24.3749 28.9217 24.4618 28.6851 24.6477H28.1506V17.7038C28.1506 16.6085 27.3607 15.6741 26.2729 15.4815C24.7426 15.2114 23.1787 15.0186 21.6216 14.9048C21.608 14.8956 21.5926 14.8858 21.5793 14.8766C21.1729 14.5958 20.6641 14.505 20.1819 14.6252C20.0291 14.6635 19.8882 14.7281 19.7542 14.8038C19.2707 14.7886 18.7371 14.7799 18.2307 14.7799C17.1501 14.7799 16.056 14.8216 14.9656 14.8979L14.1777 13.004C13.9277 12.4025 13.3658 11.9949 12.7109 11.9404L12.2865 11.9056C11.9645 11.8789 11.6455 11.9701 11.3877 12.1639C10.9512 12.4926 10.7684 13.0616 10.9328 13.5795L11.4733 15.282C11.0458 15.3451 10.6173 15.4061 10.1933 15.4808C9.10235 15.6733 8.31055 16.6081 8.31055 17.7038V25.5998C8.31055 26.2921 8.80527 26.89 9.48691 27.0212C12.3484 27.5731 15.2902 27.8527 18.2307 27.8527C20.6051 27.8527 22.9912 27.6698 25.3225 27.3089C26.41 27.1402 27.4965 27.0515 28.5591 27.0366C28.8409 27.4012 29.3037 27.5856 29.5947 27.5986C29.6236 27.5997 29.6518 27.6005 29.6791 27.6005C30.1955 27.6005 30.5088 27.377 30.6826 27.184C30.9955 26.8371 31.1182 26.291 31.0201 25.6861C30.9924 25.5155 30.9635 25.3454 30.9342 25.1752C30.9156 25.0661 30.8991 24.9616 30.8812 24.8548C36.7748 22.2755 37.3815 18.5081 37.6674 12.6396ZM21.0076 15.6888C21.0729 15.7339 21.1493 15.7814 21.2198 15.8278L21.2212 15.8287C21.747 16.1746 22.4043 16.5461 23.1185 16.7884H19.5353L19.9563 15.9599C20.0512 15.7731 20.2231 15.6362 20.4275 15.5851C20.6307 15.5341 20.8363 15.5712 21.0076 15.6888ZM18.2307 15.77C18.4606 15.77 18.6964 15.7721 18.9307 15.7754L18.4161 16.7884H15.7521L15.3686 15.8666C16.3212 15.807 17.2805 15.77 18.2307 15.77ZM12.9996 16.7884L12.3893 14.8657L13.7972 14.6886L14.6707 16.7884H12.9996ZM11.9928 12.9521C12.0529 12.9069 12.1254 12.8852 12.2027 12.8922L12.6275 12.9274C12.9066 12.9506 13.1465 13.1242 13.2531 13.3806L13.5605 14.1194L12.2065 14.2897L11.8867 13.2824C11.8484 13.1617 11.8912 13.0287 11.9928 12.9521ZM25.1678 26.3304C20.0645 27.121 14.7371 27.025 9.67795 26.0496C9.46503 26.0087 9.31055 25.8195 9.31055 25.5999V17.7038C9.31055 17.0889 9.75567 16.5637 10.3685 16.4554C10.8347 16.3732 11.3065 16.3045 11.7763 16.2368L12.1557 17.4319C12.2211 17.6381 12.4143 17.7785 12.6326 17.7785H24.2088C24.692 17.7785 25.0853 17.389 25.0853 16.9102C25.0853 16.6424 24.9648 16.4042 24.7749 16.2451C25.2177 16.3097 25.659 16.3789 26.0971 16.4562C26.7076 16.564 27.1506 17.0889 27.1506 17.7038V25.1427C27.1506 25.4161 27.3744 25.6378 27.6506 25.6378H29.0967C29.2088 25.6378 29.3002 25.7283 29.3002 25.8393C29.3002 25.9502 29.2088 26.0404 29.0967 26.0404H28.8654C27.6545 26.0404 26.4107 26.1378 25.1678 26.3304Z" fill="black"/></svg>`,
  'straw-lid-hull': `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M38.6376 11.8228C38.6509 11.5498 38.438 11.3178 38.1622 11.3042C37.8888 11.2895 37.652 11.5018 37.6384 11.7749C37.3518 17.6663 36.6577 20.7613 31.7048 23.0186C31.7042 23.0126 31.7031 23.0065 31.7024 23.0005L31.6806 22.7905C31.652 22.5182 31.4001 22.3183 31.1321 22.3488C30.8571 22.3767 30.6575 22.6199 30.686 22.8918L30.7079 23.1018C30.7182 23.2005 30.734 23.2977 30.7466 23.3959C30.6622 23.4214 30.5763 23.4477 30.4989 23.4696C30.1883 23.5581 29.8919 23.645 29.6553 23.8309H29.1208V16.8871C29.1208 15.7917 28.331 14.8574 27.2431 14.6647C25.6673 14.3867 24.0558 14.1887 22.4448 14.0761L20.8681 13.9914C20.3493 13.9732 19.7572 13.9631 19.2009 13.9631C18.2274 13.9631 17.2346 13.9993 16.2446 14.0615L14.8515 11.4241C14.6802 11.0489 14.2413 10.837 13.7966 10.9182L12.429 11.1685C12.1677 11.216 11.939 11.3727 11.8018 11.5982C11.6648 11.824 11.6318 12.0974 11.7136 12.3542L12.4127 14.4635C12.0025 14.5241 11.5901 14.5888 11.1636 14.664C10.0726 14.8566 9.28076 15.7914 9.28076 16.8871V24.7831C9.28076 25.4754 9.77548 26.0733 10.4571 26.2044C13.3186 26.7563 16.2604 27.0359 19.2009 27.0359C21.5753 27.0359 23.9614 26.853 26.2927 26.4922C27.3802 26.3234 28.4668 26.2347 29.5294 26.2198C29.8111 26.5844 30.2739 26.7689 30.565 26.7818C30.5938 26.783 30.622 26.7838 30.6493 26.7838C31.1657 26.7838 31.479 26.5602 31.6528 26.3672C31.9657 26.0203 32.0884 25.4742 31.9904 24.8693C31.9626 24.6988 31.9337 24.5286 31.9044 24.3584C31.8858 24.2493 31.8693 24.1448 31.8514 24.0381C37.745 21.4587 38.3517 17.6913 38.6376 11.8228ZM13.9732 11.8975L15.5106 14.8079L16.1454 15.9716H13.965L12.6909 12.1276L13.9732 11.8975ZM26.138 25.5137C21.0347 26.3042 15.7073 26.2083 10.6482 25.2329C10.4352 25.1919 10.2808 25.0028 10.2808 24.7831V16.8871C10.2808 16.2721 10.7259 15.7469 11.3388 15.6386C11.8146 15.5549 12.2725 15.4839 12.7291 15.4181L13.1278 16.621C13.1952 16.8244 13.3868 16.9617 13.6028 16.9617H23.5351C23.8112 16.9617 24.0351 16.7401 24.0351 16.4666C24.0351 16.1932 23.8112 15.9716 23.5351 15.9716H17.2819L16.7657 15.0256C17.5823 14.9822 18.3985 14.9533 19.2009 14.9533C19.7458 14.9533 20.3255 14.9629 20.8234 14.9803L22.3825 15.0643C23.9503 15.1737 25.5263 15.3675 27.0673 15.6394C27.6778 15.7473 28.1208 16.2721 28.1208 16.8871V24.3259C28.1208 24.5994 28.3446 24.821 28.6208 24.821H30.0669C30.179 24.821 30.2704 24.9115 30.2704 25.0225C30.2704 25.1335 30.179 25.2236 30.0669 25.2236H29.8356C28.6247 25.2236 27.381 25.3211 26.138 25.5137Z" fill="black"/></svg>`,
  'switch-lid': `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M27.9211 12.9495C27.9211 11.8431 27.1313 10.8991 26.043 10.705C20.7711 9.76499 15.2313 9.76499 9.95937 10.705C8.87109 10.8991 8.08105 11.8431 8.08105 12.9495V18.1722C8.08105 18.2954 8.13054 18.4044 8.20442 18.4915C8.13054 18.5787 8.08105 18.6877 8.08105 18.8109V27.6308C8.08105 28.33 8.57577 28.9339 9.25741 29.0663C12.1189 29.6237 15.0607 29.9062 18.0012 29.9062C20.9414 29.9062 23.8836 29.6237 26.7453 29.0663C27.4266 28.9339 27.9211 28.33 27.9211 27.6308V18.8933C27.9211 18.773 27.873 18.6669 27.8022 18.5806C27.873 18.4943 27.9211 18.3882 27.9211 18.2679V12.9495ZM10.135 11.6894C15.2912 10.7699 20.7109 10.7699 25.8672 11.6894C26.4777 11.7984 26.9211 12.3283 26.9211 12.9495V18.1688C25.015 18.5215 23.066 18.7579 21.1207 18.8809C20.3219 18.0808 19.2184 17.5851 18.0012 17.5851C16.7839 17.5851 15.6804 18.0808 14.8816 18.8809C12.9362 18.758 10.9873 18.5215 9.08105 18.1688V12.9495C9.08105 12.3283 9.52441 11.7984 10.135 11.6894ZM18.0012 18.5851C19.8817 18.5851 21.4117 20.1152 21.4117 21.9956C21.4117 23.8765 19.8817 25.4066 18.0012 25.4066C16.1205 25.4066 14.5906 23.8765 14.5906 21.9956C14.5906 20.1152 16.1205 18.5851 18.0012 18.5851ZM26.9211 27.6308C26.9211 27.8527 26.7668 28.0437 26.5539 28.0851C20.9563 29.1754 15.0461 29.1754 9.44845 28.0851C9.23553 28.0437 9.08105 27.8527 9.08105 27.6308V18.8109C9.08105 18.7985 9.07489 18.7881 9.07401 18.776C10.8285 19.0967 12.6174 19.3195 14.4069 19.4486C13.8949 20.1689 13.5906 21.0466 13.5906 21.9956C13.5906 24.4277 15.5691 26.4066 18.0012 26.4066C20.4332 26.4066 22.4117 24.4277 22.4117 21.9956C22.4117 21.0466 22.1075 20.1689 21.5955 19.4486C23.3907 19.319 25.1856 19.0954 26.9454 18.7731C26.9353 18.8126 26.9211 18.8506 26.9211 18.8933V27.6308Z" fill="black"/><path d="M13.8888 14.113C15.2453 14.2349 16.6283 14.297 17.9994 14.297C19.3709 14.297 20.7537 14.2349 22.1099 14.113C22.2747 14.0981 22.3966 13.9524 22.3818 13.7872C22.3669 13.622 22.2236 13.5009 22.056 13.5153C19.3795 13.7563 16.6197 13.7567 13.9427 13.5153C13.7736 13.5009 13.6316 13.622 13.6169 13.7872C13.6021 13.9525 13.7238 14.0981 13.8888 14.113Z" fill="black"/></svg>`,
  'grip-lid': `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M25.1499 11.2741C24.6831 11.0895 24.1616 10.9999 23.5558 10.9999H12.4456C11.8398 10.9999 11.3182 11.0895 10.8516 11.2741C9.16825 11.9399 8.08057 13.5366 8.08057 15.3419V28.3423C8.08057 29.0415 8.57529 29.6454 9.25693 29.7778C12.1184 30.3352 15.0602 30.6176 18.0007 30.6176C20.9409 30.6176 23.8831 30.3352 26.7448 29.7778C27.4261 29.6454 27.9206 29.0415 27.9206 28.3423V15.3419C27.9206 13.5364 26.8331 11.9397 25.1499 11.2741ZM26.9206 28.3423C26.9206 28.5641 26.7663 28.7552 26.5534 28.7966C20.9558 29.8868 15.0456 29.8868 9.44797 28.7966C9.23505 28.7552 9.08057 28.5641 9.08057 28.3423V15.3419C9.08057 13.9495 9.92001 12.7178 11.2192 12.2042C11.5669 12.0667 11.9679 11.9999 12.4456 11.9999H23.5558C24.0335 11.9999 24.4347 12.0667 24.7819 12.2042C26.0812 12.7178 26.9206 13.9495 26.9206 15.3419V28.3423Z" fill="black"/><path d="M22.5461 14.0515H13.4557C11.1604 14.0515 10.834 14.6726 10.834 16.9668V18.1527C10.834 18.9898 11.5149 19.6707 12.3519 19.6707C12.6281 19.6707 12.8519 19.4468 12.8519 19.1707C12.8519 18.9653 12.7276 18.7896 12.5506 18.7126C16.1594 18.4433 19.8416 18.4433 23.451 18.7126C23.2739 18.7896 23.1496 18.9653 23.1496 19.1707C23.1496 19.4468 23.3735 19.6707 23.6496 19.6707C24.4867 19.6707 25.1676 18.9898 25.1676 18.1527V16.9668C25.1676 14.6726 24.8414 14.0515 22.5461 14.0515ZM11.834 17.7658V16.9668C11.834 15.0515 11.8949 15.0515 13.4557 15.0515H22.5461C24.1067 15.0515 24.1676 15.0515 24.1676 16.9668V17.7658C20.0904 17.4257 15.9103 17.4257 11.834 17.7658Z" fill="black"/><path d="M22.0555 20.7268C19.379 20.9682 16.6192 20.9682 13.9423 20.7268C13.7731 20.7123 13.6311 20.8334 13.6165 20.9986C13.6016 21.1639 13.7233 21.3096 13.8883 21.3244C15.2448 21.4463 16.6278 21.5084 17.9989 21.5084C19.3704 21.5084 20.7532 21.4463 22.1095 21.3244C22.2743 21.3096 22.3962 21.1638 22.3813 20.9986C22.3665 20.8334 22.2231 20.7123 22.0555 20.7268Z" fill="black"/></svg>`,
  'lid-hull': `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M27.041 15.5054C21.7656 14.5648 16.2334 14.5648 10.9574 15.5054C9.86894 15.6999 9.0791 16.6437 9.0791 17.7499V25.7249C9.0791 26.4241 9.57382 27.0281 10.2555 27.1605C13.117 27.7179 16.0588 28.0003 18.9992 28.0003C21.2223 28.0003 23.4567 27.8382 25.6406 27.5191C26.3379 27.4171 27.0453 27.2964 27.7434 27.1605C28.4246 27.0281 28.9191 26.4241 28.9191 25.7249V17.7499C28.9191 16.6437 28.1293 15.6999 27.041 15.5054ZM27.9191 25.7249C27.9191 25.9468 27.7649 26.1378 27.5519 26.1792C26.8695 26.312 26.1777 26.43 25.4961 26.5292C20.5344 27.255 15.3611 27.1363 10.4465 26.1792C10.2336 26.1378 10.0791 25.9468 10.0791 25.7249V17.7499C10.0791 17.1288 10.5225 16.5987 11.133 16.4898C13.7131 16.03 16.3596 15.7968 18.9992 15.7968C21.6387 15.7968 24.2855 16.03 26.8652 16.4898C27.4758 16.5987 27.9191 17.1288 27.9191 17.7499V25.7249Z" fill="black"/><path d="M18.8566 16.4592C17.0541 16.4592 14.9053 16.6065 14.9053 17.3073C14.9053 18.0081 17.0541 18.1549 18.8566 18.1549C20.6594 18.1549 22.8082 18.0081 22.8082 17.3073C22.8082 16.6065 20.6594 16.4592 18.8566 16.4592ZM18.8566 17.5549C17.3686 17.5549 16.3502 17.428 15.8326 17.3073C16.35 17.1862 17.3684 17.0592 18.8566 17.0592C20.3449 17.0592 21.3633 17.1862 21.8809 17.3073C21.3633 17.428 20.345 17.5549 18.8566 17.5549Z" fill="black"/><path d="M24.6472 17.1469C24.1385 17.1469 23.7261 17.2169 23.7261 17.3033C23.7261 17.3897 24.1385 17.4597 24.6472 17.4597C25.156 17.4597 25.5684 17.3897 25.5684 17.3033C25.5684 17.2169 25.156 17.1469 24.6472 17.1469Z" fill="black"/></svg>`,
};
const SHUFFLE_ICON = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" stroke="black" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const svgUri = (s) => 'data:image/svg+xml,' + encodeURIComponent(s);
// AU's jK(): placement -> class token, e.g. "Middle" -> "middle".
const handleize = (s) => String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

// EU renamed the whole ceramic range: `customisable-ceramic-reusable-bottle-20oz-595ml` is now
// `ceramic-lined-reusable-water-bottle-20oz-592ml-flip-straw-lid` (new prefix, different ml
// rounding, lid name appended). The position CSS ported from AU in fg-customiser-layout.css is
// keyed on the OLD handles, and the stage's container class comes from the cart-parent handle — so
// an unmapped handle means NONE of the per-part rules match, `--image-scale` is never set, and every
// `transform: scale(var(--image-scale)) …` declaration is invalid and dropped. Result: lids render
// unscaled and unpositioned at full canvas size. Normalise EU -> AU by oz size (same physical
// products, so the AU offsets are correct). See _scopeHandle.
const CERAMIC_HANDLE_BY_SIZE = {
  'water-bottle': {
    // The 12oz "water bottle" is not a bottle body: its `base` component is a Ceramic Reusable
    // Cup Base - 12oz / 355ml (AU product 7188309901371, NZ 7160598364230), the same vessel the
    // AU 12oz CUP block is cut for — so it maps across to the cup handle, not a bottle one
    // (there is no 12oz ceramic bottle). Same one-vessel-two-names case the 16oz cup/bottle pair
    // documents in fg-customiser-layout.css. That AU block sets --image-scale .74 and covers
    // lid-hull / straw-lid-hull / flip-straw-lid-hull / switch-lid / grip-lid / grip-and-sip-lid,
    // so every lid this product offers is positioned. Keyed by size, so the 12oz siblings with
    // other lid suffixes (-straw-lid, -push-button-lid) resolve through the same entry.
    // Reported broken on NZ and AU; the handle is shared, so expect the other regions too.
    12: 'customisable-ceramic-reusable-cup-12oz-355ml',
    16: 'customisable-ceramic-reusable-bottle-16oz-475ml',
    20: 'customisable-ceramic-reusable-bottle-20oz-595ml',
    34: 'customisable-ceramic-reusable-bottle-34oz-1-000ml',
    68: 'customisable-ceramic-reusable-bottle-68oz-2-000ml',
  },
  cup: {
    8: 'customisable-ceramic-reusable-cup-8oz-230ml',
    10: 'customisable-ceramic-reusable-cup-10oz-295ml',
    12: 'customisable-ceramic-reusable-cup-12oz-355ml',
  },
};

// One-off EU handles with no position block of their own, outside the `ceramic-lined-…` renaming
// pattern above. Chrome 34oz: AU's own `chrome-ceramic-reusable-bottle-34oz-1-000ml` block carries
// ONLY monogram/emoji offsets (top:42% / 44%) — the same values as the ceramic 34oz — and no lid
// transforms at all, because on AU it isn't lid-customisable. On EU it is (its customiser data has a
// `lid` component with four lid types), so it needs a full block: same 34oz body, so the ceramic 34oz
// offsets apply unchanged.
// Original 25oz grip & sip: NZ carries TWO products for the same physical bottle — the net-new combo
// (`740ml-25oz-original-bottle-with-grip-sip-lid`, which the `[class*="740ml-25oz-original"]` block
// positions) and an older single-variant twin used as the Fuel & Flow bundle's member. The twin's
// handle matches no block, so its base AND lid both drew unscaled at full canvas. Same body, so it
// reuses the combo's block.
const HANDLE_ALIASES = {
  '34oz-chrome-bottle-with-flip-straw-lid': 'customisable-ceramic-reusable-bottle-34oz-1-000ml',
  'customisable-original-reusable-bottle-with-grip-and-sip-lid-25oz-740ml': '740ml-25oz-original-bottle-with-grip-sip-lid',
};

// A composite lid product carries the LID's part_type (`flip-straw-lid`), while the drawn layer and
// the picker icon are keyed on the HULL's (`flip-straw-lid-hull`). Map the outer names to their hull
// so `_partClass` doesn't fall through to its `includes('straw')` rule — which would class a flip
// straw lid as the never-drawn straw part. Exact match only, so distinct lids whose part_type simply
// ends in "-lid" (e.g. `party-cup-lid`, `switch-lid`) keep their own class.
const LID_TYPE_TO_HULL = {
  'flip-straw-lid': 'flip-straw-lid-hull',
  'straw-lid': 'straw-lid-hull',
  lid: 'lid-hull',
};
// Recolour an emoji SVG to the engraved silver look the monogram uses (matches the text: fill silver,
// thin grey stroke). The emoji SVGs have NO fill attr on their paths (default black), so set fill on the
// root <svg> (inherited by fill-less paths) AND rewrite any explicit fill="" / fill: style declarations.
const recolorEmoji = (svg) => String(svg)
  .replace(/<svg\b/i, '<svg fill="silver"')
  .replace(/fill="(?!none)[^"]*"/gi, 'fill="silver"')
  .replace(/fill:\s*(?!none)[^;}"']+/gi, 'fill:silver')
  .replace(/<path /gi, '<path stroke="#979797" stroke-width="1" ');
// The AU monogram curve path (viewBox 0 0 211 6) — a gentle arc matching the bottle's curvature.
const MONO_CURVE = 'M0.902344 1C15.5221 3 42.5802 7.02724 107.893 6.9999C173.207 6.97255 199.483 3 211.902 1';

// Latin letters that carry no combining mark, so NFD leaves them whole and an ASCII-only
// filter would delete them outright — "Łukasz" would become "ukasz". Folding them by hand
// keeps the name readable. Uppercase and lowercase are listed separately so the shopper's
// own casing survives; the engraver is fed exactly what they see in the field.
const LATIN_FOLD = {
  'Ł': 'L', 'ł': 'l', 'Ø': 'O', 'ø': 'o', 'Đ': 'D', 'đ': 'd', 'Ð': 'D', 'ð': 'd',
  'Þ': 'TH', 'þ': 'th', 'ß': 'ss', 'ẞ': 'SS', 'Æ': 'AE', 'æ': 'ae', 'Œ': 'OE', 'œ': 'oe',
  'Ħ': 'H', 'ħ': 'h', 'Ŋ': 'N', 'ŋ': 'n', 'Ŧ': 'T', 'ŧ': 't', 'Ə': 'E', 'ə': 'e',
  'ı': 'i', 'İ': 'I', 'Ĳ': 'IJ', 'ĳ': 'ij', 'Ŀ': 'L', 'ŀ': 'l',
};

// Reduce Latin text to bare ASCII letters: fold the no-mark letters above, then NFD so every
// remaining accent splits into a base letter plus a combining mark. The caller's character
// class then drops the marks and keeps the base — "JOSÉ" lands on "JOSE", not "JOS".
const foldToAscii = (s) =>
  String(s || '').replace(/[^ -]/g, (ch) => (ch in LATIN_FOLD ? LATIN_FOLD[ch] : ch)).normalize('NFD');

class FgCustomiser extends LitElement {
  static properties = {
    productUrl: { attribute: 'data-product-url' },
    cartRoot:   { attribute: 'data-cart-root' },
    layoutCss:  { attribute: 'data-layout-css' },
    selectedVariant: { attribute: 'data-selected-variant' },  // #18: PDP's selected variant id at launch
    // Multi-item model: the customiser is always a LIST of items (a plain product is a list of one; a
    // gift set is many). Each item = { kind, data, sel, mono, extras, ...group meta }. `_focus` is the
    // item currently being edited. The single-product render/logic below operates on the focused item
    // via the _data/_sel/_mono/_extras getters, so it needs no per-item awareness.
    _items:     { state: true },
    _focus:     { state: true },
    _visited:   { state: true },   // gift set: { [itemIndex]: true } — items the shopper has opened at least once
    _giftExtras:{ state: true },   // set-level add_ons for a gift set: { [addOnId]: variantId }
    _open:      { state: true },   // { [accordionKey]: bool } — overrides default open state
    _loading:   { state: true },
    _error:     { state: true },
    _adding:    { state: true },
    _swatchOrder:  { state: true },   // colour-order dropdown selection (see SWATCH_CONTROLS)
    _hideSoldOut:  { state: true },   // "Hide sold out" checkbox
  };

  // The focused item and its state. Every existing method reads/writes these; the getters/setters route
  // to _items[_focus] so the single-product code drives whichever gift-set item is focused.
  get _it()    { return this._items && this._items[this._focus] || null; }
  get _data()  { return this._it ? this._it.data : null; }
  get _sel()   { return this._it ? this._it.sel : {}; }
  set _sel(v)  { if (this._it) { this._it.sel = v; this.requestUpdate(); } }
  get _mono()  { return this._it ? this._it.mono : {}; }
  set _mono(v) { if (this._it) { this._it.mono = v; this.requestUpdate(); } }
  get _extras(){ return this._it ? this._it.extras : {}; }
  set _extras(v){ if (this._it) { this._it.extras = v; this.requestUpdate(); } }
  get _isGiftSet() { return !!this._giftSet; }

  static styles = css`
    /* Type comes from the THEME, not from here. The panel used to hardcode 'DM Sans' and #1a1a1a, so opening
       the customiser visibly changed the font and the ink colour against the buy block right next to it
       (client-reported on the Snack Container PDP). The theme sets --font-body-family: 'Circular Std' on
       :root; custom properties inherit through the shadow boundary, so referencing it here picks up whatever
       the theme uses — including any future font change — with the old stack kept only as a fallback.
       Measured buy-block values are mirrored below: title 24/500/1.33, price 18/700/1px,
       labels 16/500/.4px, button 16/400/1px, ink #000. */
    :host { display:block; --fg-accent:#009739; --fg-line:#e6e4e0; --fg-muted:#8a857f; --fg-ink:#000;
            font-family:var(--font-body-family,'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif);
            color:var(--fg-ink); }

    /* Full-bleed like the main buy block: grey image area fills the left, fixed white panel on the right. */
    .shell { display:flex; align-items:stretch; width:100%; min-height:78vh; }
    @media (max-width:900px){ .shell{ flex-direction:column; min-height:0; } }

    /* .stage is just the 4:5 positioning context the ported AU layout CSS (fg-customiser-layout.css,
       injected into this shadow root at runtime) fills absolutely — a per-product --image-scale +
       per-part transform/z-index aligns the part shots to each other. */
    .stageWrap { flex:1 1 auto; position:relative; background:#eceae7; display:flex; align-items:center;
                 justify-content:center; padding:24px; min-height:56vh; overflow:hidden; }
    .stage { position:relative; height:min(74vh, 780px); aspect-ratio:4/5; width:auto; max-width:100%; margin:auto; }
    /* Mobile: pin the product image to the top so choosing a colour further down the panel doesn't scroll
       it out of view (mirrors the locked-product behaviour of the current site). The opaque stage background
       keeps panel content from showing through as it scrolls beneath. */
    @media (max-width:900px){
      .stageWrap { position:sticky; top:0; z-index:3; min-height:0; height:46vh; padding:12px; }
      .stage { height:100%; width:auto; max-width:100%; }
    }
    .rand { position:absolute; right:20px; bottom:20px; display:inline-flex; align-items:center; gap:7px;
            padding:9px 15px; border:1px solid var(--fg-line); border-radius:999px; background:#fff; font:inherit;
            font-size:13px; font-weight:500; cursor:pointer; box-shadow:0 1px 3px rgba(0,0,0,.08); }
    .rand img { width:15px; height:15px; }

    /* Width is derived from the theme's own buy block so the sidebar doesn't jump when the customiser opens.
       Measured on the PDP at 3 viewports: the media column is exactly 72% of the LAYOUT viewport, then a 24px
       gap, then the info column, then a constant 30px right gutter — i.e. buy block = 28% - 54px.
       Adding this panel's own 34px left + 30px right padding gives 28% + 10px, which puts our content column
       on the same left and right edges as the buy block's. Verified pixel-exact at 1249/1600/1920.
       % (not vw) on purpose: flex-basis % resolves against .shell = the layout viewport, whereas vw INCLUDES
       the scrollbar and was throwing the alignment out by exactly the 15px scrollbar width. */
    .panel { flex:0 0 clamp(300px, calc(28% + 10px), 679px); box-sizing:border-box;
             padding:36px 30px 44px 34px; background:#FCFBF5; }
    @media (max-width:900px){ .panel{ flex-basis:auto; padding:24px 18px 32px; } }
    /* matches .product__title h1 (24px/500/31.92px) and .price-item--regular (18px/700/1px) */
    .title { font-size:24px; font-weight:var(--font-heading-weight,500); line-height:1.33; margin:0 0 4px; }
    .price { font-size:18px; font-weight:700; letter-spacing:1px; margin:0 0 18px; }

    .styleHead { display:flex; align-items:center; flex-wrap:wrap; gap:8px 12px; padding:16px 0 10px;
                 border-top:1px solid var(--fg-line); }
    /* AU's string is literally "Style your <noun>" and the live theme title-cases it in CSS — mirror that so
       "Style your Cup" reads "Style Your Cup" like the reference, and the fixed "Style Your Bottle" is a no-op. */
    .styleHead h2 { font-size:16px; font-weight:500; letter-spacing:.4px; margin:0; text-transform:capitalize; }
    .styleHead-right { margin-left:auto; display:flex; align-items:center; flex-wrap:wrap; justify-content:flex-end; gap:10px; }
    .styleHead .x { background:none; border:0; font-size:17px; line-height:1; color:var(--fg-muted); cursor:pointer; padding:4px; }
    /* Colour-order dropdown + "Hide sold out" checkbox (bottles only; behind the SWATCH_CONTROLS flag). */
    .sw-ctrls { display:flex; align-items:center; gap:12px; }
    .sw-order { font:inherit; font-size:12px; font-weight:500; color:var(--fg-ink); padding:6px 24px 6px 9px;
                border:1px solid var(--fg-line); border-radius:8px; background:#fff; cursor:pointer; appearance:none;
                background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%231a1a1a' stroke-width='1.4' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
                background-repeat:no-repeat; background-position:right 9px center; }
    .sw-order:focus { outline:none; border-color:var(--fg-ink); }
    .sw-hide { display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:500; color:var(--fg-ink);
               cursor:pointer; white-space:nowrap; user-select:none; }
    .sw-hide input { width:15px; height:15px; margin:0; accent-color:var(--fg-ink); cursor:pointer; }

    .acc { border-top:1px solid var(--fg-line); }
    .acc:last-of-type { border-bottom:1px solid var(--fg-line); }
    .acc-h { display:flex; align-items:center; gap:8px; width:100%; padding:15px 2px; background:none; border:0;
             font:inherit; cursor:pointer; text-align:left; color:var(--fg-ink); }
    /* matches the buy block's variant labels (.form__label 16px/500/.4px/20.8px) */
    .acc-label { font-size:16px; font-weight:500; letter-spacing:.4px; line-height:1.3; }
    .acc-val { color:var(--fg-muted); font-size:16px; font-weight:400; letter-spacing:.4px; line-height:1.3; }
    .acc-ico { margin-left:auto; font-size:18px; line-height:1; width:16px; text-align:center; color:var(--fg-ink); }
    .acc-body { padding:2px 0 20px; }

    .types { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
    .type { position:relative; display:flex; align-items:center; gap:9px; padding:8px 12px; border:1px solid var(--fg-line);
            border-radius:10px; background:#faf9f8; font:inherit; font-size:13px; font-weight:500; cursor:pointer;
            color:var(--fg-ink); text-align:left; min-height:44px; box-sizing:border-box; }
    /* Surcharge badges (match the live site): on a lid TYPE button, and above a colour SWATCH. */
    .type-sur { position:absolute; top:-8px; left:10px; background:#f0efec; color:var(--fg-ink); font-size:10px;
                font-weight:600; padding:1px 6px; border-radius:8px; line-height:1.5; }
    .type.on { border-color:var(--fg-ink); box-shadow:inset 0 0 0 1px var(--fg-ink); background:#fff; }
    .type[disabled]{ opacity:.4; cursor:not-allowed; }
    .type-ico { width:26px; height:26px; flex:0 0 auto; }
    /* "?" info marker in the corner of a lid-type button; hover reveals the lid's part_tooltip. */
    .type-info { position:absolute; top:6px; right:6px; width:16px; height:16px; border-radius:50%; border:1px solid var(--fg-muted);
                 color:var(--fg-muted); font-size:10px; font-weight:700; line-height:14px; text-align:center; background:#fff;
                 cursor:help; z-index:2; }
    .type-tip { position:absolute; top:calc(100% + 7px); right:-4px; width:max-content; max-width:210px; white-space:normal;
                background:var(--fg-ink); color:#fff; font-size:11px; font-weight:400; line-height:1.35; text-align:left;
                padding:8px 10px; border-radius:7px; box-shadow:0 4px 14px rgba(0,0,0,.2); opacity:0; pointer-events:none;
                transform:translateY(3px); transition:opacity .12s ease, transform .12s ease; z-index:7; }
    .type-tip::after { content:''; position:absolute; bottom:100%; right:9px; border:5px solid transparent; border-bottom-color:var(--fg-ink); }
    .type-info:hover .type-tip, .type-info:focus-visible .type-tip { opacity:1; transform:translateY(0); }

    .swatches { display:flex; flex-wrap:wrap; gap:10px; padding-top:15px; }
    .sw { width:32px; height:32px; border-radius:50%; border:2px solid transparent; box-shadow:0 0 0 1px rgba(0,0,0,.14) inset;
          cursor:pointer; padding:0; background-size:cover; background-position:center; position:relative;
          transition:outline-color .12s ease, transform .12s ease, opacity .15s ease, filter .15s ease; }
    /* Hover: a clear ring around the swatch (outline doesn't shift layout) + a subtle lift. */
    .sw:not([disabled]):hover { outline:2px solid var(--fg-ink); outline-offset:2px; transform:scale(1.08); z-index:4; }
    .sw-sur { position:absolute; bottom:calc(100% + 3px); left:50%; transform:translateX(-50%); background:#f0efec;
              color:var(--fg-ink); font-size:9px; font-weight:600; line-height:1.6; padding:0 4px; border-radius:6px;
              white-space:nowrap; pointer-events:none; z-index:2; transition:opacity .12s ease; }
    /* Hover name bubble: floats above the swatch with a little pointer. Hidden until hover/focus. */
    .sw-tip { position:absolute; bottom:calc(100% + 9px); left:50%; transform:translateX(-50%) translateY(3px); background:var(--fg-ink);
              color:#fff; font-size:11px; font-weight:600; line-height:1; letter-spacing:.01em; padding:6px 9px; border-radius:7px;
              white-space:nowrap; pointer-events:none; opacity:0; z-index:6; box-shadow:0 4px 12px rgba(0,0,0,.18);
              transition:opacity .12s ease, transform .12s ease; }
    .sw-tip::after { content:''; position:absolute; top:100%; left:50%; transform:translateX(-50%); border:5px solid transparent;
              border-top-color:var(--fg-ink); }
    .sw:hover .sw-tip, .sw:focus-visible .sw-tip { opacity:1; transform:translateX(-50%) translateY(0); }
    /* When the name bubble is up, get the surcharge pill out of its way. */
    .sw:hover .sw-sur { opacity:0; }
    /* Selected swatch: a detached brand-green ring (white gap + green) so it reads clearly on any colour. */
    .sw.on { border-color:transparent; box-shadow:0 0 0 1px rgba(0,0,0,.14) inset, 0 0 0 2px #fff, 0 0 0 4px var(--fg-accent); }
    /* Sold out: the colour circle should read as very faded/transparent while the strike stays crisp and
       opaque. We can't use opacity on the button (that would dim the strike too), so a near-opaque white
       veil (::before) washes out the circle and the strike (::after) is layered opaque on top. */
    .sw[disabled] { cursor:not-allowed; filter:grayscale(.5); }
    .sw[disabled]::before { content:''; position:absolute; inset:0; border-radius:50%; background:#fff; opacity:.82; z-index:1; }
    .sw[disabled]::after { content:''; position:absolute; left:50%; top:50%; width:150%; height:1px; background:#111;
          transform:translate(-50%,-50%) rotate(-45deg); border-radius:2px; z-index:2; }

    .badge { display:inline-block; margin-left:8px; padding:1px 7px; border-radius:999px; background:#f0efec;
             color:var(--fg-ink); font-size:11px; font-weight:600; vertical-align:middle; }

    /* Monogramming */
    .mono { display:flex; flex-direction:column; gap:10px; }
    .mono-h { font-size:13px; font-weight:600; margin-top:4px; }
    .mono-back-wrap { margin-top:14px; padding-top:14px; border-top:1px dashed var(--fg-line); }
    .mono-l { font-size:12px; font-weight:600; color:var(--fg-muted); text-transform:uppercase; letter-spacing:.03em; }
    .mono-count { color:var(--fg-muted); font-weight:500; text-transform:none; letter-spacing:0; }
    .mono-in { width:100%; box-sizing:border-box; padding:11px 12px; border:1px solid var(--fg-line); border-radius:8px;
               font:inherit; font-size:15px; }
    .mono-in:focus { outline:none; border-color:var(--fg-ink); }
    .mono-in.bad { border-color:#c0392b; }
    .mono-warn { color:#c0392b; font-size:12px; }
    .mono-pl { display:flex; flex-direction:column; gap:6px; }
    .pills { display:flex; flex-wrap:wrap; gap:8px; }
    .pill { padding:7px 13px; border:1px solid var(--fg-line); border-radius:999px; background:#fff; font:inherit;
            font-size:13px; cursor:pointer; color:var(--fg-ink); }
    .pill.on { border-color:var(--fg-ink); font-weight:600; }
    .mono-em { display:flex; flex-direction:column; gap:6px; }
    .emojis { display:flex; flex-wrap:wrap; gap:8px; }
    .emoji { width:40px; height:40px; display:flex; align-items:center; justify-content:center; padding:4px;
             border:1px solid var(--fg-line); border-radius:8px; background:#fff; cursor:pointer; font-size:12px; }
    .emoji.on { border-color:var(--fg-ink); box-shadow:inset 0 0 0 1px var(--fg-ink); }
    .emoji img { width:100%; height:100%; object-fit:contain; }
    .mono-note { font-size:13px; color:var(--fg-ink); margin:12px 0 0; line-height:1.5; }
    .mono-disc { font-size:11px; color:var(--fg-muted); margin:4px 0 0; line-height:1.4; }
    /* Emoji on the BOTTLE preview (inside --emojis) — silver engraved, sized in container-query units
       like the AU (.emoji svg { height:4cqw }); we render <img>, so size that instead. */
    .fg-cust__dynamic-image--emojis { gap:1cqw; }
    .fg-cust__dynamic-image--emojis .emoji { width:5cqw; height:5cqw; border:0; background:none; padding:0; }
    .fg-cust__dynamic-image--emojis .emoji img { width:100%; height:100%; object-fit:contain; }

    /* Extras */
    .extras { display:flex; flex-direction:column; gap:12px; }
    .extra { border:1px solid var(--fg-line); border-radius:10px; padding:12px; }
    .extra.on { border-color:var(--fg-ink); }
    .extra-h { display:flex; align-items:center; gap:10px; }
    .extra-ico { width:28px; height:28px; flex:0 0 auto; }
    .extra-t { font-size:14px; font-weight:600; }
    .extra-p { color:var(--fg-muted); font-size:13px; }
    .extra-add { margin-left:auto; padding:7px 14px; border:1px solid var(--fg-ink); border-radius:999px; background:#fff;
                 font:inherit; font-size:13px; font-weight:600; cursor:pointer; color:var(--fg-ink); }
    .extra-add.on { background:var(--fg-ink); color:#fff; }
    .extra-tip { font-size:12px; color:var(--fg-muted); margin:8px 0 0; line-height:1.4; }
    .extra .swatches { margin-top:12px; }
    /* Composite extra (e.g. an extra lid) — one labelled colour row per inner part (hull/strap/straw/button). */
    .extra-colour { margin-top:12px; display:flex; flex-direction:column; gap:6px; }
    .extra-colour .mono-l { margin:0; }

    /* Gift set: a row of item thumbnails (switch which product you're editing) + product-group options. */
    .giftset { padding:14px 0 6px; border-bottom:1px solid var(--fg-line); margin-bottom:6px; }
    .giftset-items { display:flex; flex-wrap:wrap; gap:10px; }
    .giftset-item { display:flex; flex-direction:column; align-items:center; gap:5px; width:84px; padding:8px 6px; background:none;
                    border:1px solid var(--fg-line); border-radius:10px; cursor:pointer; font:inherit; color:var(--fg-ink); position:relative; }
    .giftset-item.on { border-color:var(--fg-ink); box-shadow:inset 0 0 0 1px var(--fg-ink); }
    /* The thumb is the 4:5 positioning frame (mirrors .stage). The layered image is a CHILD .fg-cust__dynamic-image
       — it must NOT be the thumb element itself, or the injected layout CSS (.fg-cust__dynamic-image{position:absolute;
       top:0;bottom:0;width:100%}) yanks the thumb out of flow and it overlaps the label. overflow:hidden clips any
       lid translate that would spill past the frame into the label. */
    .giftset-thumb { position:relative; width:64px; height:80px; overflow:hidden; container-type:inline-size; }
    .giftset-thumb .fg-cust__dynamic-image { position:absolute; inset:0; width:100%; }
    .giftset-thumb .fg-cust__dynamic-image--product { position:absolute; inset:0; }
    /* The injected layout CSS defaults --elem to opacity:0 (the main stage forces opacity:1 inline; these
       thumbnails don't), so force it on here or every thumbnail layer renders invisible. */
    .giftset-thumb .fg-cust__dynamic-image--elem { position:absolute; inset:0; opacity:1; }
    .giftset-thumb .fg-cust__dynamic-image--elem--inner, .giftset-thumb img { width:100%; height:100%; object-fit:contain; }
    .giftset-options { margin-top:14px; display:flex; flex-direction:column; gap:6px; }
    /* Product-group options mirror the live site's .gift-set-buttons__group-option: full-width rounded
       rectangle rows (NOT pills), stacked 12px apart, sand fill, and the selection shown as a 3px inset
       outline rather than a border so the row doesn't shift by a pixel when picked. The surcharge tag
       floats out of the top-left corner, so the list needs headroom for it. */
    .giftset-option-list { display:flex; flex-direction:column; gap:12px; padding-top:10px; }
    .giftset-option { position:relative; box-sizing:border-box; width:100%; min-height:40px; display:flex;
                      align-items:center; justify-content:flex-start; gap:12px; padding:12px; border:0;
                      border-radius:6px; background:#f3f2e9; font:inherit; font-size:12px; font-weight:700;
                      line-height:18px; text-align:left; color:var(--fg-ink); cursor:pointer;
                      transition:outline-color .3s; }
    .giftset-option:hover { outline:1px solid #777772; outline-offset:-1px; }
    .giftset-option.on, .giftset-option.on:hover { outline:3px solid #191c19; outline-offset:-3px; }
    /* Surcharge tag: pinned to the row's top-left corner, outlined on the panel background (the shared
       .badge pill would read as an inline chip). */
    .giftset-option .badge { position:absolute; top:-10px; left:-6px; margin:0; padding:2px 4px; border-radius:6px;
                      background:#fcfbf5; border:1px solid #c5c6c7; color:var(--fg-ink); font-size:12px;
                      line-height:12px; font-weight:400; }

    /* matches the theme's Add to cart button (16px/400/1px) */
    .atc { margin-top:26px; width:100%; padding:16px; border:0; border-radius:8px; background:var(--fg-accent); color:#fff;
          font:inherit; font-size:16px; font-weight:400; letter-spacing:1px; cursor:pointer; }
    .atc[disabled] { opacity:.6; cursor:progress; }
    /* Sold out: the greyed-out disabled state of the theme's own Add to cart, and not the cursor:progress
       above, which would read as "still working" on a button that is never going to submit. */
    .atc--soldout { background:#d5d2ce; color:#5f5b56; }
    .atc--soldout[disabled] { opacity:1; cursor:not-allowed; }
    .atc--blocked { background:#d5d2ce; color:#5f5b56; }
    /* Gift-set "visit the other item first" nudge — outlined so it doesn't read as the final CTA. */
    .atc--gating { background:#fff; color:var(--fg-ink); border:1px solid var(--fg-ink); }
    .status { padding:60px 24px; text-align:center; color:var(--fg-muted); }

    /* Lid switching: ease the jolt when layers change. New layers (e.g. a lid type that adds a strap
       layer) fade in; a reused layer whose position class changes eases its transform instead of
       snapping. Scoped to .stage so gift-set thumbnails + the capture canvas are unaffected. Nodes
       that lit reuses (only the colour/src changed) don't re-run the animation, so picking a colour
       stays instant — only a genuine layer change animates. */
    @keyframes fgLayerIn { from { opacity:0; } to { opacity:1; } }
    @media (prefers-reduced-motion: no-preference) {
      .stage .fg-cust__dynamic-image--elem { animation: fgLayerIn .3s ease; transition: transform .3s ease; }
    }
  `;

  constructor() {
    super();
    this._loading = true; this._error = ''; this._open = {}; this._adding = false;
    this._items = []; this._focus = 0; this._visited = { 0: true }; this._giftExtras = {};
    this._giftSet = null;   // { variant_id, title, handle, addOns } when this product is a gift set
    this._swatchOrder = SWATCH_CONTROLS.DEFAULT_ORDER;
    this._hideSoldOut = SWATCH_CONTROLS.DEFAULT_HIDE_SOLD_OUT;
    // Variant ids of sold-out colours that have been selected at some point, so hide-sold-out keeps
    // showing them (disabled) instead of making them disappear on the next pick — see _orderVariants.
    // Not a reactive property: it's only ever grown during render, from the selection that render reads.
    this._stickySoldOut = new Set();
  }

  connectedCallback() { super.connectedCallback(); this._load(); }

  firstUpdated() { this._injectLayoutCss(); }

  // The AU positioning system lives in fg-customiser-layout.css (light-DOM asset). Shadow DOM doesn't
  // inherit page styles, so fetch it once and adopt it into this shadow root. Falls back to a <style>.
  async _injectLayoutCss() {
    if (!this.layoutCss || this._layoutDone) return;
    this._layoutDone = true;
    try {
      const res = await fetch(this.layoutCss);
      if (!res.ok) return;
      const cssText = await res.text();
      const root = this.renderRoot;
      if (root.adoptedStyleSheets && 'replaceSync' in CSSStyleSheet.prototype) {
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(cssText);
        root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
      } else {
        const style = document.createElement('style');
        style.textContent = cssText;
        root.appendChild(style);
      }
    } catch (e) { console.warn('[FG] layout css load failed', e); }
  }

  // NOTE: request as text/html — with Accept: application/json Shopify ignores ?view= and returns its
  // built-in product JSON (no components/price) instead of rendering the template.
  async _fetchJson(url) {
    const res = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error('customiser data ' + res.status);
    return res.json();
  }
  _memberUrl(handle) { return cartRootOf(this.cartRoot) + 'products/' + handle + '?view=customiser'; }

  async _load() {
    try {
      const base = (this.productUrl || location.pathname).split('?')[0];
      const data = await this._fetchJson(base + '?view=customiser');
      const groups = data.giftProductGroups || [];
      const fixed = data.giftProducts || [];
      if (groups.length || fixed.length) {
        await this._loadGiftSet(data, groups, fixed);
      } else {
        this._items = [this._makeItem('single', data)];
        this._focus = 0;
        // #18: open on the PDP colourway. Use the launcher-supplied selected variant (set at "Customise"
        // click), else a ?variant= deep link. A gift set has no single PDP variant, so this only runs here.
        const vid = this._pendingColourway || this.selectedVariant || new URLSearchParams(location.search).get('variant');
        if (vid) this._applyColourwayById(vid);
      }
      this._loading = false;
      this._preloadImages();
    } catch (e) { this._error = e.message; this._loading = false; console.error('[FG] customiser load failed', e); }
  }

  // Warm the browser cache with EVERY lid/colour render up front, so switching lids or colours later is
  // instant instead of triggering an on-click fetch (the images the stage actually shows are otherwise
  // requested only when first rendered). Best-effort: walk all datasets for the big customiser/featured
  // image URLs, dedup, and fetch them via detached Image objects (kept referenced so they aren't GC'd
  // mid-download). Swatch thumbnails/emoji aren't included — they're tiny and already load with the panel.
  _preloadImages() {
    try {
      const urls = new Set();
      const walk = (node) => {
        if (!node || typeof node !== 'object') return;
        if (Array.isArray(node)) { for (const n of node) walk(n); return; }
        if (typeof node.customiser_image === 'string') urls.add(node.customiser_image);
        if (typeof node.featured_image === 'string') urls.add(node.featured_image);
        for (const k in node) { const v = node[k]; if (v && typeof v === 'object') walk(v); }
      };
      (this._items || []).forEach((it) => walk(it.data));
      this._preloaded = (this._preloaded || []).concat([...urls].map((u) => { const im = new Image(); im.decoding = 'async'; im.src = u; return im; }));
    } catch (e) { /* preload is best-effort — never block the customiser on it */ }
  }

  _makeItem(kind, data, extra) {
    return Object.assign({ kind, data, sel: this._seedSel(data), mono: {}, extras: {} }, extra || {});
  }

  // Gift set: fetch each member product's own customiser JSON (product groups first, then fixed products —
  // same order as AU), then build one bundle item per slot. A product-group slot carries its options so
  // the shopper can swap which product fills it; fixed slots are always in the set.
  async _loadGiftSet(setData, groups, fixed) {
    const groupHandles = [];
    groups.forEach((g) => (g.products || []).forEach((o) => groupHandles.push(o.product && o.product.handle)));
    const fixedHandles = fixed.map((f) => f.handle);
    const handles = [...new Set([...groupHandles, ...fixedHandles].filter(Boolean))];
    const fetched = {};
    await Promise.all(handles.map(async (h) => {
      try { fetched[h] = await this._fetchJson(this._memberUrl(h)); }
      catch (e) { console.warn('[FG] gift member load failed', h, e); fetched[h] = null; }
    }));

    const url = new URL(window.location.href);
    const items = [];
    groups.forEach((g, gi) => {
      const options = (g.products || []).map((o, oi) => {
        const h = o.product && o.product.handle;
        const d = fetched[h] || null;
        return {
          id: o.id || ('group_' + gi + '_option_' + oi),
          label: (o.label && o.label.trim()) || (d && d.product && d.product.title) || (o.product && o.product.title) || h,
          handle: h,
          surcharge: Number(o.surcharge || 0),
          data: d,
        };
      }).filter((o) => o.data);
      if (!options.length) return;
      // Deep-link restore: ?group-<n>-product=<handle> selects that option (matches AU).
      const param = url.searchParams.get('group-' + (gi + 1) + '-product');
      let idx = 0;
      if (param) { const i = options.findIndex((o) => o.handle === param); if (i >= 0) idx = i; }
      const chosen = options[idx];
      items.push(this._makeItem('group', chosen.data, { groupId: g.id, options, selectedOptionId: chosen.id }));
    });
    fixed.forEach((f) => { const d = fetched[f.handle]; if (d) items.push(this._makeItem('fixed', d)); });

    if (!items.length) { this._items = [this._makeItem('single', setData)]; this._focus = 0; return; }
    this._items = items;
    this._focus = 0;
    this._visited = { 0: true };   // failsafe: shopper must open every item before the set can be added to cart
    this._giftSet = {
      variant_id: setData.product.variant_id,
      title: setData.product.title,
      handle: setData.product.handle,
      price: Number(setData.product.price) || 0,
      addOns: (setData.add_ons && setData.add_ons.length) ? setData.add_ons : null,
      // Driven entirely by data: null on every store without the
      // frank_green.gift_set_packaging metafield, so this stays inert on the
      // regions that have not been mapped yet (live on UK + AU).
      packagingVid: setData.product.packaging_variant_id || null,
    };
  }

  // #18: start the customiser on the PDP's selected colourway. Called by the launcher (fg-customiser.liquid)
  // at the "Customise" click with the product form's current variant id, and at load for a ?variant= deep
  // link. Reseeds EVERY component's colour to the selected variant's colour name (confirmed w/ client), so a
  // solid "Lilac Haze" bottle opens all-Lilac-Haze; components without that colour keep their default. If the
  // data isn't loaded yet (clicked mid-load), it's stashed and applied when _load finishes. Gift sets: no-op.
  applyColourway(variantId) {
    if (!variantId) return;
    if (this._loading || !this._data || this._isGiftSet) { this._pendingColourway = variantId; return; }
    this._pendingColourway = null;
    this._applyColourwayById(variantId);
  }
  _applyColourwayById(variantId) {
    // Preferred: the combo's preselected-bundle part variants, keyed by the COMBO variant id (the id the PDP
    // form/URL reports). These pin the exact part for each component — right LID TYPE + every colour + base,
    // not just a colour name. (The top-level `variants` array is base_product/PARENT variants, which do NOT
    // contain the combo variant id — that mismatch is why the earlier lookup silently no-op'd.)
    const bundle = ((this._data.preset_bundles || {})[String(variantId)] || []).map((x) => String(x)).filter(Boolean);
    if (bundle.length) { this._applyPresetBundle(new Set(bundle)); return; }
    // Fallback (colour-name) for a customisable product opened directly, where the selected variant IS one of
    // its own variants.
    const v = ((this._data.product && this._data.product.variants) || this._data.variants || []).find((x) => String(x.id) === String(variantId));
    if (!v) return;
    const name = (v.colour_swatch && v.colour_swatch.name) || (v.variant_options && v.variant_options[0]) || v.title;
    if (!name) return;
    const pick = (variants) => (variants || []).find((x) => x.available && (((x.colour_swatch && x.colour_swatch.name) || x.title) === name));
    const sel = { ...this._sel };
    (this._data.components || []).forEach((c) => {
      const cur = sel[c.name]; if (!cur) return;
      if (cur.assembly) { const parts = { ...(cur.parts || {}) }; (c.products || []).forEach((p) => { const m = pick(p.variants); if (m) parts[p.id] = m.id; }); sel[c.name] = { assembly: true, parts }; return; }
      const p = (c.products || []).find((x) => x.id === cur.productId) || (c.products || [])[0];
      if (!p) return;
      const ips = innerProducts(p);
      if (ips) {
        const inner = { ...(cur.inner || {}) };
        ips.forEach((ip) => { const m = pick(ip.variants); if (m) inner[ip.id] = m.id; });
        sel[c.name] = { ...cur, inner };
      } else {
        const m = pick(p.variants); if (m) sel[c.name] = { ...cur, variantId: m.id };
      }
    });
    this._sel = sel;
  }

  // Configure every component from a set of preselected-bundle part-variant ids: pick the product in each
  // component that owns one of these variants (its own variant, or an inner-part variant → also selects the
  // right lid TYPE), and set its colour(s) to the bundle. Components not represented keep their selection.
  _applyPresetBundle(idSet) {
    const sel = { ...this._sel };
    (this._data.components || []).forEach((c) => {
      if (this._isAssemblyComp(c)) {
        const parts = { ...((sel[c.name] || {}).parts || {}) };
        (c.products || []).forEach((p) => { const m = (p.variants || []).find((x) => idSet.has(String(x.id))); if (m) parts[p.id] = m.id; });
        sel[c.name] = { assembly: true, parts };
        return;
      }
      for (const p of (c.products || [])) {
        const ips = innerProducts(p);
        if (ips) {
          // The hull is the type-defining part. strap/straw variants are SHARED across lid types, so match the
          // lid TYPE on the hull only — otherwise an earlier composite lid (e.g. Flip Straw, first in the list)
          // false-matches a Straw/Switch lid's shared strap/straw variant and the WRONG lid type is preselected
          // when landing on that lid's PDP. (BUG-1) If a composite lid has no hull part, fall back to any match.
          const hullIp = (ips || []).find((ip) => this._partRole(ip) === 'hull');
          const hullMatch = hullIp ? (hullIp.variants || []).some((x) => idSet.has(String(x.id))) : true;
          if (!hullMatch) continue;
          const inner = {};
          ips.forEach((ip) => { const m = (ip.variants || []).find((x) => idSet.has(String(x.id))); inner[ip.id] = m ? m.id : (this._firstAvail(ip) || {}).id; });
          sel[c.name] = { productId: p.id, variantId: (this._firstAvail(p) || {}).id, inner };
          break;
        } else {
          const m = (p.variants || []).find((x) => idSet.has(String(x.id)));
          if (m) { sel[c.name] = { productId: p.id, variantId: m.id }; break; }
        }
      }
    });
    this._sel = sel;
  }

  // Seed a per-component selection for a product's customiser data. Honours default_parts (by part_type ->
  // product and variant id) when present, else falls back to the first product + first available variant.
  _seedSel(data) {
    const sel = {};
    const dp = (data && data.default_parts) || [];
    ((data && data.components) || []).forEach((c) => {
      if (this._isAssemblyComp(c)) {
        const parts = {}; (c.products || []).forEach((p) => { const v = this._firstAvail(p); if (v) parts[p.id] = v.id; });
        sel[c.name] = { assembly: true, parts };
        return;
      }
      let p = (c.products || [])[0];
      if (dp.length) {
        const m = (c.products || []).find((pp) => dp.some((d) => d.part_type === pp.part_type)
          || (innerProducts(pp) || []).some((ip) => dp.some((d) => d.part_type === ip.part_type)));
        if (m) p = m;
      }
      if (!p) return;
      sel[c.name] = this._selEntryFrom(p, dp);
    });
    return sel;
  }
  // Build a selection entry for a product: its own variant + (for composite lids) a default per inner part.
  _selEntry(p) { return this._selEntryFrom(p, null); }
  _selEntryFrom(p, dp) {
    const entry = { productId: p.id, variantId: (this._firstAvail(p) || {}).id };
    if (dp && dp.length) { const d = dp.find((x) => x.part_type === p.part_type); if (d) { const v = (p.variants || []).find((v) => v.id === d.id); if (v) entry.variantId = v.id; } }
    const ips = innerProducts(p);
    if (ips) {
      entry.inner = {};
      ips.forEach((ip) => {
        let v = this._firstAvail(ip);
        if (dp && dp.length) { const d = dp.find((x) => x.part_type === ip.part_type); if (d) { const vv = (ip.variants || []).find((v) => v.id === d.id); if (vv) v = vv; } }
        if (v) entry.inner[ip.id] = v.id;
      });
    }
    return entry;
  }
  // Inner-part accessors (composite lids).
  _innerProductsOf(c) { return innerProducts(this._productFor(c)); }
  _innerVar(c, ip) { return innerVariant(ip, this._sel, c.name); }
  _pickInner(c, ip, v) {
    if (!v.available) return;
    const s = this._sel[c.name] || {};
    this._sel = { ...this._sel, [c.name]: { ...s, inner: { ...(s.inner || {}), [ip.id]: v.id } } };
  }
  _innerLabel(ip) {
    const pt = (ip.part_type || '').toLowerCase();
    // Check hull FIRST — 'flip-straw-lid-hull' contains the substring 'straw'.
    if (pt.includes('hull')) return 'Lid Colour';
    if (pt.includes('strap')) return 'Strap Colour';
    if (pt.includes('button')) return 'Button Colour';
    if (pt.includes('straw')) return 'Straw Colour';
    return 'Lid Colour';
  }
  // The generic ROLE of a part (hull / strap / straw / button), collapsing all lid-hull variants
  // (flip-straw-lid-hull, straw-lid-hull, switch-lid, lid-hull…) to 'hull'. Used to carry a colour across a
  // lid-TYPE change: match the old part to the new part by role (#21), not by the type-specific class.
  _partRole(ip) {
    const pt = (ip.part_type || '').toLowerCase();
    if (pt.includes('hull')) return 'hull';
    if (pt.includes('strap')) return 'strap';
    if (pt.includes('button')) return 'button';
    if (pt.includes('straw')) return 'straw';
    if (pt.includes('switch') || pt.includes('lid')) return 'hull';
    return 'hull';
  }
  // Layout-CSS class for an inner-part layer (the hull -> its lid class; else strap/button/straw).
  _innerClass(ip) {
    const pt = (ip.part_type || '').toLowerCase();
    // Hull/lid classes FIRST — they contain substrings like 'straw' (flip-straw-lid-hull).
    if (pt.includes('switch')) return 'switch-lid';
    for (const k of ['flip-straw-lid-hull', 'grip-and-sip-lid', 'straw-lid-hull', 'grip-lid', 'lid-hull']) if (pt.includes(k)) return k;
    if (pt.includes('strap')) return 'strap';
    if (pt.includes('button')) return 'button';
    if (pt.includes('straw')) return 'straw';
    return pt || 'lid-hull';
  }

  _productFor(c) { const s = this._sel[c.name]; return (c.products || []).find((p) => p.id === s?.productId) || (c.products || [])[0]; }
  _variantFor(c) { const s = this._sel[c.name]; const p = this._productFor(c); return (p?.variants || []).find((v) => v.id === s?.variantId) || null; }

  _pick(c, p, v) { if (!v.available) return; this._sel = { ...this._sel, [c.name]: { productId: p.id, variantId: v.id } }; }

  // pick a different PRODUCT within a multi-product component (e.g. switch lid type), PRESERVING the
  // shopper's current colour where the new product offers it (match by colour name; per inner part for
  // composite lids) so switching lid type doesn't reset the lid/strap/button colours.
  _firstAvail(p) { return (p?.variants || []).find((v) => v.available) || (p?.variants || [])[0] || null; }
  _pickProduct(c, p) { if (!this._firstAvail(p) && !innerProducts(p)) return; this._sel = { ...this._sel, [c.name]: this._selEntryLike(c, p) }; }

  // Entry for switching component `c` to product `p`, keeping the current colour(s) when possible — across
  // ANY lid-type transition, including composite<->flat (e.g. Flip Straw <-> Grip lid). We read the current
  // colour by ROLE (hull/strap/straw/button) from the old product — a composite's inner parts, or a flat
  // product's own variant as the 'hull' role — then apply it to the new product's matching role(s). New roles
  // with no old colour (e.g. a strap when coming from a strapless lid) inherit the hull colour.
  _selEntryLike(c, p) {
    const prev = this._sel[c.name];
    const oldProd = prev ? (c.products || []).find((x) => x.id === prev.productId) : null;
    const nameOf = (variants, id) => { const v = (variants || []).find((x) => x.id === id); return v ? ((v.colour_swatch && v.colour_swatch.name) || v.title) : null; };
    const matchByName = (variants, want) => want && (variants || []).find((v) => v.available && (((v.colour_swatch && v.colour_swatch.name) || v.title) === want));
    const wantByRole = {};
    if (oldProd) {
      const oldIps = innerProducts(oldProd);
      if (oldIps && prev && prev.inner) { oldIps.forEach((ip) => { const nm = nameOf(ip.variants, prev.inner[ip.id]); if (nm) wantByRole[this._partRole(ip)] = nm; }); }
      else { const nm = nameOf(oldProd.variants, prev.variantId); if (nm) wantByRole.hull = nm; }
    }
    const ips = innerProducts(p);
    if (ips) {
      const entry = { productId: p.id, variantId: (this._firstAvail(p) || {}).id, inner: {} };
      ips.forEach((ip) => {
        const want = wantByRole[this._partRole(ip)] || wantByRole.hull;
        const v = matchByName(ip.variants, want) || this._firstAvail(ip);
        if (v) entry.inner[ip.id] = v.id;
      });
      return entry;
    }
    const v = matchByName(p.variants, wantByRole.hull) || this._firstAvail(p);
    return { productId: p.id, variantId: (v || {}).id };
  }

  // Single product: base price + surcharges + monogram + extras. Gift set: the set's own price + every
  // item's surcharges/monogram/extras + each product-group option surcharge + set-level extras.
  _totalCents() {
    if (!this._isGiftSet) return bundleCents(this._data, this._sel, this._mono, this._extras);
    let c = this._giftSet.price;
    this._items.forEach((it) => { c += this._itemExtraCents(it) + this._optSurchargeCents(it); });
    if (this._giftSet.addOns) resolveExtras({ add_ons: this._giftSet.addOns }, this._giftExtras).forEach((e) => { c += Math.round(e.priceCents || 0); });
    return c;
  }
  // Surcharges + monogram + extras for ONE item (NOT its base product price — the set price covers that).
  _itemExtraCents(it) {
    let c = 0;
    resolve(it.data, it.sel).forEach((p) => { c += Math.round((p.surcharge || 0) * 100); });
    resolveMono(it.data, it.sel, it.mono).forEach((m) => { c += Math.round(m.priceCents || 0); });
    resolveExtras(it.data, it.extras).forEach((e) => { c += Math.round(e.priceCents || 0); });
    return c;
  }
  // The chosen product-group option's surcharge (dollars -> cents), 0 for fixed items.
  _selectedOption(it) { return it.options ? it.options.find((o) => o.id === it.selectedOptionId) : null; }
  _optSurchargeCents(it) { const o = this._selectedOption(it); return o ? Math.round((o.surcharge || 0) * 100) : 0; }

  _money(cents) { return money(cents, (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD'); }

  // Focus a different item in the set for editing (resets which accordion rows are open implicitly via keys).
  // Also records the item as "visited" — the add-to-cart failsafe requires every item opened at least once.
  _focusItem(i) {
    if (!this._items[i]) return;
    this._focus = i;
    if (!this._visited[i]) this._visited = { ...this._visited, [i]: true };
  }

  // Add-to-cart failsafe (gift set only): true once the shopper has opened every item at least once, so
  // they can't buy the set having only seen the first product. Single products are never gated.
  _allVisited() { return !this._isGiftSet || this._items.every((_, i) => this._visited[i]); }
  // Focus the next item the shopper hasn't opened yet (drives the "Customise additional product →" button).
  _focusNextUnvisited() {
    const n = this._items.length;
    for (let k = 1; k <= n; k++) { const i = (this._focus + k) % n; if (!this._visited[i]) { this._focusItem(i); return; } }
  }

  // Swap which product fills a product-group slot. Preserves the shopper's per-option selections so
  // flipping back and forth keeps their colours/monogram (mirrors AU's productSelectionCache).
  _selectGroupOption(i, optId) {
    const it = this._items[i]; if (!it || !it.options) return;
    const opt = it.options.find((o) => o.id === optId); if (!opt || opt.id === it.selectedOptionId) return;
    it.cache = it.cache || {};
    it.cache[it.selectedOptionId] = { sel: it.sel, mono: it.mono, extras: it.extras };
    const cached = it.cache[optId];
    const next = { ...it, data: opt.data, selectedOptionId: optId,
      sel: cached ? cached.sel : this._seedSel(opt.data),
      mono: cached ? cached.mono : {},
      extras: cached ? cached.extras : {} };
    this._items = this._items.map((x, idx) => (idx === i ? next : x));
    this._focus = i;
  }

  // Layers for a specific item (used by the gift-set item thumbnails, which aren't the focused stage).
  _layerPartsFor(it) {
    const out = [];
    ((it.data && it.data.components) || []).forEach((c) => {
      const s = it.sel[c.name]; if (!s) return;
      if (s.assembly) {
        (c.products || []).forEach((p) => {
          if (this._partRole(p) === 'straw') return;   // #27: the loose straw isn't shown on a standalone lid (kept in cart / on bottles)
          const v = (p.variants || []).find((x) => x.id === (s.parts || {})[p.id]);
          const src = v && (v.customiser_image || v.featured_image);
          if (src) out.push({ src, cls: this._posClass(this._partClass(c, p)) });
        });
        return;
      }
      const p = (c.products || []).find((x) => x.id === s.productId) || (c.products || [])[0];
      if (!p) return;
      const ips = innerProducts(p);
      if (ips) {
        ips.forEach((ip) => {
          const cls = this._innerClass(ip);
          if (cls === 'straw') return;   // straw is never drawn in the customiser (kept in cart)
          const v = innerVariant(ip, it.sel, c.name); if (!v) return;
          const sub = cls === 'strap' || cls === 'button';
          const src = sub ? (v.customiser_image && v.customiser_image !== v.featured_image ? v.customiser_image : null) : (v.customiser_image || v.featured_image);
          if (src) out.push({ src, cls: this._posClass(cls) });
        });
      } else {
        const v = (p.variants || []).find((x) => x.id === s.variantId) || null;
        const src = v && (v.customiser_image || v.featured_image);
        if (src) out.push({ src, cls: this._partClass(c, p) });
      }
    });
    return out;
  }

  _layers() {
    return (this._data?.components || [])
      .map((c) => this._variantFor(c))
      .map((v) => v && (v.customiser_image || v.featured_image))
      .filter(Boolean);
  }

  // Part-type class the AU layout CSS keys on (.fg-cust__dynamic-image--<class>). The base component
  // maps to "base"; lids use the chosen product's part_type (with the AU switch-lid alias).
  _partClass(c, p) {
    const name = (c.name || '').toLowerCase();
    // A To Go Bowl's body component is named `bowl`, not `base`, so it fell through to its own part_type
    // (`to-go-bowl-small-400ml`) as the layer class. AU's customiser classes that layer `--base`, and the
    // ported AU position rules are keyed on `--base`, so the bowl body got no transform at all. Treat `bowl`
    // as the base layer, matching AU. (Return value is only ever used as a CSS class — see render().)
    if (name === 'base' || name.includes('base') || name === 'bowl') return 'base';
    const pt = ((p && p.part_type) || '').toLowerCase();
    if (pt.includes('switch')) return 'switch-lid';
    if (LID_TYPE_TO_HULL[pt]) return LID_TYPE_TO_HULL[pt];
    // Match the AU class the layout CSS keys on. Order matters: more specific first, and
    // 'lid-hull' last since it is a substring of several others (e.g. flip-straw-LID-HULL).
    const LID = ['flip-straw-lid-hull', 'grip-and-sip-lid', 'straw-lid-hull', 'grip-lid', 'lid-hull'];
    for (const k of LID) if (pt.includes(k)) return k;
    // Secondary parts: some sizes name the strap/straw part_type verbosely (e.g. 68oz `reusable-straw-lid-strap-68oz`)
    // rather than the bare `strap`/`straw`. Map to the canonical class so assembly detection + the layout CSS
    // (which keys on --strap/--straw/--button) still work. Order: strap before straw (the 68oz strap contains both). (BUG-9)
    if (pt.includes('strap')) return 'strap';
    if (pt.includes('button')) return 'button';
    if (pt.includes('straw')) return 'straw';
    return pt || 'lid-hull';
  }

  // Positioning class for a rendered layer. A push-button lid is a composite whose `button` inner part is a
  // registered layer co-shot with the lid hull on the SAME canvas (…PUSH_BUTTON_LID_HULL_* / …_BUTTON_*),
  // but the ported AU layout CSS has no `--button` transform, so the raw class would render it unscaled/
  // unpositioned (floating). Map it to the hull's class so it inherits the per-product hull transform;
  // DOM order (button after hull) keeps it painted on top. `_innerClass` still returns 'button' for the
  // sub-layer detection + colour-row label, so only the emitted position class changes.
  _posClass(cls) { return cls === 'button' ? 'lid-hull' : cls; }

  // A standalone accessory lid (e.g. `switch-lid-with-strap-straw-34oz`) is wired as ONE component whose flat
  // products are the PARTS of a single assembly (hull + strap + straw), not interchangeable lid TYPES. The
  // default "component with >1 product = pick one type" rule would render only one part; instead we STACK them
  // (a layer + colour row per part). Detected by a bare `strap` product sibling — bottles/cups never expose a
  // flat strap at component level (their strap lives inside a composite lid), so this can't fire on them.
  // Assembly selection shape: sel[c.name] = { assembly:true, parts:{ [productId]:variantId } }.
  _isAssemblyComp(c) {
    const prods = c.products || [];
    // A standalone lid whose parts are all flat (no inner composites) and include a strap OR a button should
    // STACK (assembly), not render as a lid-TYPE picker. Bottle/cup lid components hold COMPOSITE lid types
    // (innerProducts truthy) so they fail the all-flat test and are unaffected. (BUG-8: standalone button-lid.)
    return prods.length >= 2 && prods.every((p) => !innerProducts(p)) && prods.some((p) => { const k = this._partClass(c, p); return k === 'strap' || k === 'button'; });
  }
  _pickAssemblyPart(c, p, v) {
    if (!v.available) return;
    const s = this._sel[c.name] || { assembly: true, parts: {} };
    this._sel = { ...this._sel, [c.name]: { assembly: true, parts: { ...(s.parts || {}), [p.id]: v.id } } };
  }

  // Selected layer(s) per component, tagged with the part class for the positioning CSS. A composite lid
  // expands into one layer per inner part (hull + strap + straw / hull + button); everything else is one layer.
  _layerParts() {
    const out = [];
    (this._data?.components || []).forEach((c) => {
      const sa = this._sel[c.name];
      if (sa && sa.assembly) {
        (c.products || []).forEach((p) => {
          if (this._partRole(p) === 'straw') return;   // #27: hide the loose straw on a standalone lid (still in cart / on bottles)
          const v = (p.variants || []).find((x) => x.id === (sa.parts || {})[p.id]);
          const src = v && (v.customiser_image || v.featured_image);
          if (src) out.push({ src, cls: this._posClass(this._partClass(c, p)), comp: c });
        });
        return;
      }
      const p = this._productFor(c);
      const ips = innerProducts(p);
      if (ips) {
        ips.forEach((ip) => {
          const cls = this._innerClass(ip);
          if (cls === 'straw') return;   // straw is never drawn in the customiser (kept in cart)
          const v = this._innerVar(c, ip); if (!v) return;
          // Hull uses its (registered) featured image. Strap/button only render when a REGISTERED
          // on-bottle layer exists — the template falls customiser_image back to featured_image, so a real
          // registered layer shows up as customiser_image differing from the standalone featured shot.
          const sub = cls === 'strap' || cls === 'button';
          const src = sub
            ? (v.customiser_image && v.customiser_image !== v.featured_image ? v.customiser_image : null)
            : (v.customiser_image || v.featured_image);
          if (src) out.push({ src, cls: this._posClass(cls), comp: c });
        });
      } else {
        const v = this._variantFor(c);
        const src = v && (v.customiser_image || v.featured_image);
        if (src) out.push({ src, cls: this._partClass(c, p), comp: c });
      }
    });
    return out;
  }

  // Live monogram overlay on a layer — the AU curved-SVG textPath + emoji row, styled/positioned by the
  // injected layout CSS (.fg-cust__dynamic-image--monogram / --emojis, per-product top%). Only the layer
  // whose component owns the monogram renders it, and only when text/emoji are entered.
  _monoPreview(c) {
    const f = this._productFor(c)?.monogramming;
    if (!f || !f.product_id) return '';
    const st = this._mono[c.name] || {};
    const text = (st.text || '').trim();
    const codes = st.emojis || [];
    if (!text && !codes.length) return '';
    const pl = handleize(st.placement || f.placement || 'top');
    const cid = 'fgmono-' + (c.name || 'x');
    const out = [];
    if (text) out.push(html`
      <svg class="fg-cust__dynamic-image--monogram ${codes.length ? 'has-emoji' : ''} monogram-placement--${pl}"
           xmlns="http://www.w3.org/2000/svg" viewBox="0 0 211 6" fill="none">
        <path id="${cid}" d="${MONO_CURVE}"></path>
        <text dominant-baseline="middle"><textPath href="#${cid}" startOffset="50%" text-anchor="middle">${text}</textPath></text>
      </svg>`);
    if (codes.length) out.push(html`
      <div class="fg-cust__dynamic-image--emojis emojis-placement--${pl}">
        ${codes.map((code) => {
          const e = (f.emojis || []).find((x) => x.code === code);
          const svg = e && /^\s*<svg/.test(e.svg_code || '') ? recolorEmoji(e.svg_code) : '';
          return svg ? html`<div class="emoji"><img src="${svgUri(svg)}" alt=""></div>` : '';
        })}
      </div>`);
    return out;
  }

  // --- accordion state -------------------------------------------------------
  _isOpen(key, def) { return key in this._open ? this._open[key] : !!def; }
  _toggle(key, def) { this._open = { ...this._open, [key]: !this._isOpen(key, def) }; }

  _isBase(c) { return c.base_component === true || (c.name || '').toLowerCase().includes('base'); }
  // Whether a (non-composite) component actually offers a colour choice — >1 available variant.
  // Whether a part offers a colour choice — >1 variant total (out-of-stock colours are shown disabled, not
  // hidden, so a low-stock lid like grip-lid still gets a colour row rather than vanishing).
  _hasColours(c) { return (this._productFor(c)?.variants || []).length > 1; }
  _icon(cls) { const s = LID_ICONS[cls] || LID_ICONS[cls + '-hull']; return s ? html`<img class="type-ico" src="${svgUri(s)}" alt="">` : ''; }

  // Tidy lid product name for display: "Flip Straw Lid with Strap & Straw - 34oz / 1,000ml" -> "Flip Straw Lid".
  _lidName(p) {
    let n = (p && (p.part_title || p.title)) || '';
    n = n.replace(/\s+with\s+strap.*$/i, '')  // drop "with Strap & Straw - 34oz / 1,000ml"
         .replace(/\s*[-–—].*$/, '')          // drop any trailing " - <size>" suffix
         .replace(/\bhull\b/ig, '')
         .replace(/\s{2,}/g, ' ').trim();
    return n || 'Lid';
  }

  // --- surcharge badges (surface variant.surcharge_price, matching the live site) --------------------------
  // Floor surcharge of a part (dollars) = the cheapest of its variants — the unavoidable cost of that part.
  _floorSurcharge(variants) { const a = (variants || []).map((v) => Number(v.surcharge_price || 0)); return a.length ? Math.min(...a) : 0; }
  // Base surcharge for choosing a lid TYPE = sum of its parts' floor surcharges (e.g. Switch +$4 from its hull).
  _lidBaseSurcharge(tp) { const ips = innerProducts(tp); return ips ? ips.reduce((s, ip) => s + this._floorSurcharge(ip.variants), 0) : this._floorSurcharge(tp.variants); }
  _surBadge(dollars, cls) { return dollars > 0 ? html`<span class="${cls}">+${this._money(Math.round(dollars * 100))}</span>` : ''; }

  // Panel heading, named to the actual product: "Style your <focused item>" for a gift set, "Style your <Lid>"
  // for a standalone accessory lid, "Style your Bowl/Cup/Food Container/Name Tag/…" per product type, else
  // "Style Your Bottle".
  _styleHeading(strings) {
    // Gift set: AU names the heading after the FOCUSED item, not the set — "Style your Cup" while a cup is
    // selected, "Style your Straw Pack Pouch" on the pouch (verified against the live party-cup 4-pack).
    // Same short_title-then-title source as the item label. Falls back to the set wording if a set somehow
    // has no focused item.
    if (this._isGiftSet) {
      const label = this._it ? String(this._giftItemLabel(this._it, this._focus) || '').trim() : '';
      return label ? ('Style your ' + label) : tstr(strings.style_your_gift_set, 'Build Your Gift Set');
    }
    const comps = (this._data && this._data.components) || [];
    if (!comps.some((c) => this._isBase(c))) {
      const asm = comps.find((c) => this._isAssemblyComp(c));
      if (asm) return 'Style your ' + (asm.display_title || asm.name || 'Lid');
    }
    const noun = this._productNoun();
    return (!noun || noun === 'Bottle') ? tstr(strings.style_your_bottle, 'Style Your Bottle') : ('Style your ' + noun);
  }
  // The product's type noun, from its handle/title, for the heading. Order matters (specific first).
  _productNoun() {
    const p = (this._data && this._data.product) || {};
    const own = ((p.handle || '') + ' ' + (p.title || '')).toLowerCase();
    const s = (p.cart_parent_handle || '').toLowerCase() + ' ' + own;
    if (/pet[\s-]*bowl/.test(s)) return 'Pet Bowl';
    if (/\bbowl\b/.test(s)) return 'Bowl';
    if (/lunch[\s-]*container/.test(s)) return 'Lunch Container';
    if (/food[\s-]*container|insulated[\s-]*food/.test(s)) return 'Food Container';
    if (/name[\s-]*tag|collar/.test(s)) return 'Name Tag';
    if (/french[\s-]*press/.test(s)) return 'French Press';
    if (/\bjug\b|pitcher/.test(s)) return 'Jug';
    // Cup vs Bottle: trust the product's OWN title/handle (the customer-facing name) first — a 12/16oz bottle's
    // cart-parent handle can say "cup" (it resolves to a cup parent), which used to win and mislabel it. (BUG-4)
    if (/\bbottle\b/.test(own)) return 'Bottle';
    if (/\bcup\b/.test(own)) return 'Cup';
    if (/\bbottle\b/.test(s)) return 'Bottle';
    if (/\bcup\b/.test(s)) return 'Cup';
    return null;
  }

  // The swatch-order + hide-sold-out controls (and the reordering they drive) are shown ONLY where the
  // heading reads "Style Your Bottle" — i.e. bottles, per the client ask — and only when the feature flag
  // is on. Mirrors the bottle branch of _styleHeading so the two never disagree.
  _isBottleHeading() {
    if (!SWATCH_CONTROLS.ENABLED || this._isGiftSet) return false;
    const comps = (this._data && this._data.components) || [];
    if (!comps.some((c) => this._isBase(c)) && comps.find((c) => this._isAssemblyComp(c))) return false;
    const noun = this._productNoun();
    return !noun || noun === 'Bottle';
  }

  // Dropdown (colour order) + "Hide sold out" checkbox. Empty unless this is a bottle + the flag is on.
  _swatchControls() {
    if (!SWATCH_CONTROLS.SHOW_CONTROLS || !this._isBottleHeading()) return '';
    const opts = [['featured', 'Featured'], ['rainbow', 'Rainbow'], ['light', 'Light to dark'], ['dark', 'Dark to light'], ['vivid', 'Most vivid']];
    return html`
      <div class="sw-ctrls">
        <select class="sw-order" aria-label="Colour order" @change=${(e) => { this._swatchOrder = e.target.value; }}>
          ${opts.map(([v, l]) => html`<option value=${v} ?selected=${this._swatchOrder === v}>${l}</option>`)}
        </select>
        <label class="sw-hide">
          <input type="checkbox" .checked=${this._hideSoldOut} @change=${(e) => { this._hideSoldOut = e.target.checked; }}> Hide sold out
        </label>
      </div>`;
  }

  // Order a colour row's variants for display: apply the chosen order mode and (when "Hide sold out" is on)
  // drop sold-out swatches — but only while ≥1 available colour remains, and never the currently-selected
  // one (keepId), so a selector can never be emptied. Native passthrough when the feature is off / not a
  // bottle, so cups/bowls/gift sets are unchanged.
  //
  // A sold-out swatch that has been SELECTED (e.g. opening on a sold-out preselected bundle) also stays
  // visible for the rest of the session, via _stickySoldOut. Keeping it only while selected meant it
  // vanished the moment the shopper picked an in-stock colour and could never be found again — the colour
  // they had actually clicked through on silently disappeared from the row (client, 2026-07-29).
  _orderVariants(variants, keepId) {
    const vs0 = variants || [];
    if (!this._isBottleHeading()) return vs0;
    let vs = vs0;
    if (this._hideSoldOut) {
      const cur = vs0.find((v) => v.id === keepId);
      if (cur && !cur.available) this._stickySoldOut.add(cur.id);
      const anyAvail = vs.some((v) => v.available);
      if (anyAvail) vs = vs.filter((v) => v.available || v.id === keepId || this._stickySoldOut.has(v.id));
    }
    const mode = this._swatchOrder || 'light';
    return vs
      .map((v, i) => ({ v, i, hsl: swatchHsl(v.colour_swatch), chrome: isChromeVariant(v) }))
      .sort((a, b) => compareVariants(a, b, mode))
      .map((w) => w.v);
  }

  // The cart-parent handle used to scope the AU position rules (`.fg-cust__dynamic-image.<handle>`). Some Dev
  // cart-parents still carry a placeholder handle suffix (e.g. the 16oz `...-475ml-also-exists-as-cup-confirm`)
  // while the layout CSS targets the clean handle (`...-475ml`) — strip that so the rules match. (BUG-6)
  // Harmless no-op once the placeholder handle is renamed on Dev (the real fix).
  // Container handle for the AU position CSS. Strips the `-also-exists-as-…-confirm` dev suffix and the
  // `-for-gift-sets` suffix, then maps the renamed EU ceramic handles back onto the AU handle the CSS is
  // keyed on (see CERAMIC_HANDLE_BY_SIZE). Only ever used as a CSS hook — never for cart or analytics.
  // The `-for-gift-sets` cart-parents (20oz/34oz ceramic) are separate products from their retail twins
  // but share the same body, so they reuse the retail handle's position block. Without the strip they
  // match no block at all and every lid renders unscaled/unpositioned at full canvas size.
  _scopeHandle(h) {
    const base = (h || '')
      .replace(/-also-exists-as-[a-z]+-confirm$/, '')
      .replace(/-for-gift-sets$/, '');
    if (HANDLE_ALIASES[base]) return HANDLE_ALIASES[base];
    const eu = base.match(/^ceramic-lined-reusable-(water-bottle|cup)-(\d+)oz\b/);
    const alias = eu && CERAMIC_HANDLE_BY_SIZE[eu[1]] && CERAMIC_HANDLE_BY_SIZE[eu[1]][eu[2]];
    return alias || base;
  }

  render() {
    if (this._loading) return html`<div class="status">Loading customiser…</div>`;
    if (this._error) return html`<div class="status">Couldn’t load the customiser (${this._error}).</div>`;
    const d = this._data, strings = d.strings || {};
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD';
    // Container handle drives the AU position rules. Bottles use the cart-parent handle; monogram-only
    // products (bowls/tags) have no cart-parent, so fall back to the product's own handle.
    const ph = this._scopeHandle(d.product?.cart_parent_handle || d.product?.handle || '');
    return html`
      <div class="shell">
        <div class="stageWrap">
          <div class="stage">
            <div class="fg-cust__dynamic-image ${ph}">
              <div class="fg-cust__dynamic-image--product fg-cust__dynamic-image--product--${ph}">
                ${this._layerParts().map((L) => html`
                  <div class="fg-cust__dynamic-image--elem fg-cust__dynamic-image--${L.cls}" style="opacity:1">
                    <div class="fg-cust__dynamic-image--elem--inner"><img src="${L.src}" alt="" loading="lazy">${this._monoPreview(L.comp)}</div>
                  </div>`)}
              </div>
              ${this._extraStageGroups().map((g) => html`
                <div class="fg-cust__dynamic-image--add-on">
                  <div class="fg-cust__dynamic-image--is-add-on fg-cust__dynamic-image--product--${g.handle}">
                    ${g.layers.map((L) => html`<div class="fg-cust__dynamic-image--add-on--layer fg-cust__dynamic-image--add-on--${L.role}"><img src="${L.src}" alt=""></div>`)}
                  </div>
                </div>`)}
            </div>
          </div>
          <button class="rand" @click=${this._randomise} title="Randomise">
            <img src="${svgUri(SHUFFLE_ICON)}" alt=""> Randomise
          </button>
        </div>
        <div class="panel">
          <h1 class="title">${this._isGiftSet ? (this._giftSet.title || '') : (d.product?.title || '')}</h1>
          <div class="price">${money(this._totalCents(), currency)}</div>
          <div class="styleHead">
            <h2>${this._styleHeading(strings)}</h2>
            <div class="styleHead-right">
              ${this._swatchControls()}
              <button class="x" @click=${this._close} aria-label="Close">&#10005;</button>
            </div>
          </div>
          ${this._giftButtons()}
          ${this._sections()}
          ${this._atcButton(strings)}
        </div>
      </div>`;
  }

  // Colour names in the CURRENT configuration that are out of stock, from the already-loaded data (no
  // network — that's _recheckStock's job at submit time). Mirrors exactly what _addToCart would post, so
  // the button can state up front what the alert would otherwise only reveal on click. Covers every
  // gift-set item plus set-level add-ons.
  _soldOutSelected() {
    const bad = new Set();
    const checkIn = (data) => {
      const idx = variantIndex(data);
      return (id, fallback) => {
        const v = idx.get(String(id));
        if (v && v.available === false) bad.add((v.colour_swatch && v.colour_swatch.name) || v.title || fallback || 'a selected colour');
      };
    };
    (this._items || []).forEach((it) => {
      if (!it || !it.data) return;
      const check = checkIn(it.data);
      resolve(it.data, it.sel || {}).forEach((p) => check(p.id));
      resolveExtras(it.data, it.extras || {}).forEach((e) => check(e.id, e.variantTitle));
    });
    if (this._isGiftSet && this._giftSet.addOns) {
      // Stable object, so its variant index is built once rather than on every render.
      const setData = (this._giftAddOnData ||= { add_ons: this._giftSet.addOns });
      const check = checkIn(setData);
      resolveExtras(setData, this._giftExtras).forEach((e) => check(e.id, e.variantTitle));
    }
    return [...bad];
  }

  // The add-to-cart button. For a gift set it doubles as a "next product" nudge: until every item has been
  // opened, it reads "Customise additional product →" and clicking it jumps to the next unseen item; once
  // all items are visited it becomes the real Add to cart.
  //
  // When the configuration contains an out-of-stock colour the button reads "Sold out" and is disabled
  // rather than offering an Add to cart that can only fail (client, 2026-07-29) — reachable by deep-linking
  // a sold-out preselected bundle, or if a part's default colour is out of stock. The gating state wins on
  // a gift set: "Customise additional product →" still has to work, or a sold-out colour on the first item
  // would trap the shopper before they can reach the others and change it.
  //
  // A banned or over-length monogram anywhere in the configuration turns it into "Update monogram"
  // (client, 2026-09-29: the warning alone let banned text through to the 3PL). It stays clickable
  // rather than disabled, because on a gift set the offending text can sit on an item that isn't
  // focused — clicking takes the shopper straight to that input instead of leaving them hunting.
  _atcButton(strings) {
    const gating = !this._adding && !this._allVisited();
    const soldOut = this._adding || gating ? [] : this._soldOutSelected();
    const monoBad = this._adding || gating || soldOut.length ? null : this._firstMonoIssue();
    const label = this._adding
      ? 'Adding…'
      : gating
        ? tstr(strings.customise_additional_product, 'Customise additional product') + ' →'
        : soldOut.length
          ? tstr(strings.sold_out, 'Sold out')
          : monoBad
            ? tstr(this._monoStrings().blocked_button, 'Update monogram to continue')
            : tstr(strings.add_to_cart, 'Add to cart');
    return html`
      <button class="atc ${gating ? 'atc--gating' : ''} ${soldOut.length ? 'atc--soldout' : ''} ${monoBad ? 'atc--blocked' : ''}"
        ?disabled=${this._adding || soldOut.length > 0}
        title=${soldOut.length ? 'Out of stock: ' + soldOut.join(', ') : ''}
        @click=${() => (gating ? this._focusNextUnvisited() : monoBad ? this._focusMonoIssue(monoBad) : this._addToCart())}>
        ${label}
      </button>`;
  }

  // First monogram in the configuration that can't be added (see monoIssues), as { i, name, reason }
  // with `i` the gift-set item index (always 0 for a plain product), or null when every monogram is fine.
  _firstMonoIssue() {
    const banned = (t) => this._banned(t);
    const items = this._items || [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (!it || !it.data) continue;
      const found = monoIssues(it.data, it.sel || {}, it.mono || {}, banned)[0];
      if (found) return { i, ...found };
    }
    return null;
  }

  // Take the shopper to the offending monogram: focus its item, open its accordion, focus the input.
  async _focusMonoIssue(issue) {
    if (issue.i !== this._focus) this._focusItem(issue.i);
    this._open = { ...this._open, [issue.i + '|' + issue.name + ':mono']: true };
    await this.updateComplete;
    const bad = this.renderRoot.querySelector('.mono-in.bad') || this.renderRoot.querySelector('.mono-in');
    if (bad) { bad.scrollIntoView({ block: 'center', behavior: 'smooth' }); bad.focus({ preventScroll: true }); }
  }

  // Gift-set item selector: one thumbnail per item in the set (click to edit it), and — when the focused
  // item is a product group — the option buttons to swap which product fills that slot. Hidden for a
  // plain single product (one item).
  _giftButtons() {
    const it = this._it;
    const hasOptions = it && it.options && it.options.length > 1;
    const multi = this._items.length > 1;
    if (!multi && !hasOptions) return '';
    const handleOf = (item) => this._scopeHandle((item.data && item.data.product && (item.data.product.cart_parent_handle || item.data.product.handle)) || '');
    return html`
      <div class="giftset">
        ${multi ? html`<div class="giftset-items">
          ${this._items.map((item, i) => {
            // AU's tiles are image-only — no caption, no surcharge badge (the badge read as a per-tile price
            // when it was really the item's monogram cost, which is already in the header total). The label
            // survives as the tooltip + the panel heading.
            const label = this._giftItemLabel(item, i);
            const ph = handleOf(item);
            return html`<button class="giftset-item ${i === this._focus ? 'on' : ''}" aria-pressed=${i === this._focus ? 'true' : 'false'} @click=${() => this._focusItem(i)} title="${label}" aria-label="${label}">
              <div class="giftset-thumb">
                <div class="fg-cust__dynamic-image ${ph}">
                  <div class="fg-cust__dynamic-image--product fg-cust__dynamic-image--product--${ph}">
                    ${this._layerPartsFor(item).map((L) => html`<div class="fg-cust__dynamic-image--elem fg-cust__dynamic-image--${L.cls}"><div class="fg-cust__dynamic-image--elem--inner"><img src="${L.src}" alt=""></div></div>`)}
                  </div>
                </div>
              </div>
            </button>`;
          })}
        </div>` : ''}
        ${hasOptions ? html`
          <div class="giftset-options">
            <span class="mono-l">${tstr((this._data.strings || {}).choose_product, 'Choose product')}</span>
            <div class="giftset-option-list">
              ${it.options.map((o) => html`<button class="giftset-option ${o.id === it.selectedOptionId ? 'on' : ''}" aria-pressed=${o.id === it.selectedOptionId ? 'true' : 'false'} @click=${() => this._selectGroupOption(this._focus, o.id)}>
                ${o.label}${o.surcharge > 0 ? html`<span class="badge">+${this._money(Math.round(o.surcharge * 100))}</span>` : ''}
              </button>`)}
            </div>
          </div>` : ''}
      </div>`;
  }
  _giftItemLabel(item, i) {
    const p = item.data && item.data.product;
    return (p && (p.short_title || p.giftset_part_title || p.title)) || ('Item ' + (i + 1));
  }

  // Build the accordion rows: for each lid component -> Type (if >1 product) + Colour; base -> Colour.
  _sections() {
    const comps = this._data.components || [];
    const lids = comps.filter((c) => !this._isBase(c));
    const bases = comps.filter((c) => this._isBase(c));
    const rows = [];
    lids.forEach((c) => {
      const label = c.display_title || c.name;
      // Assembly (standalone lid): a colour row per PART (Lid/Strap/Straw), not a type picker.
      if (this._isAssemblyComp(c)) {
        (c.products || []).forEach((p) => { if ((p.variants || []).length > 1) rows.push(this._assemblyPartColour(c, p)); });
        return;
      }
      if ((c.products || []).length > 1) rows.push(this._typeSection(c, label + ' Type'));
      const ips = this._innerProductsOf(c);
      if (ips) {
        // Composite lid: a colour row per inner part that has a colour choice (Lid/Strap/Button Colour).
        ips.forEach((ip) => { if ((ip.variants || []).length > 1) rows.push(this._innerColourSection(c, ip)); });
      } else if (this._hasColours(c)) {
        rows.push(this._colourSection(c, label + ' Colour'));
      }
    });
    // Skip the colour row for single-variant products (e.g. monogram-only bowls / name tags).
    bases.forEach((c) => { if (this._hasColours(c)) rows.push(this._colourSection(c, this._baseColourLabel(c))); });
    // Monogramming — one row per component that carries monogram config (data-driven; absent = no row).
    comps.forEach((c) => { const m = this._monoSection(c); if (m) rows.push(m); });
    // Extras — the base product's add_ons.
    const ex = this._extrasSection(); if (ex) rows.push(ex);
    return rows;
  }

  // Colour-row label for a BASE component. Component-defined products emit a clean `display_title`
  // (e.g. "Bottle"); simple single-part products (cups, iced-coffee cups, …) emit only `name`, which is
  // the handleized product title — using it verbatim renders as "big-chill-iced-coffee-cup-24oz-710ml
  // Colour". So fall back to the product noun that already drives the heading ("Cup", "Bottle", …), and
  // to a bare "Colour" if even that is unknown.
  _baseColourLabel(c) {
    const noun = c.display_title || this._productNoun();
    return noun ? noun + ' Colour' : 'Colour';
  }

  _monoStrings() { return (this._data.strings && this._data.strings.monogramming) || {}; }

  // True if `text` contains a banned word (settings.banned_words is a comma-split list from the template).
  _banned(text) {
    const list = (this._data.settings && this._data.settings.banned_words) || [];
    const t = (text || '').toLowerCase();
    return !!t && list.some((w) => w && t.includes(String(w).trim().toLowerCase()));
  }

  // Strip characters the engraver can't render from monogram text.
  //
  // STRICT: A-Z, a-z, 0-9 and space only. Nothing else reaches the fibre laser.
  //
  // Accented Latin is folded rather than deleted, which is the whole reason this method is
  // more than one regex. "JOSÉ" engraves as "JOSE" and "MÜLLER" as "MULLER" — the accent is
  // dropped but the letter survives. A plain [^A-Za-z0-9 ] strip would have produced "JOS"
  // and "MLLER", silently eating letters on a personalised, non-returnable product; that
  // exact bug shipped once at the NA launch and must not come back. See foldToAscii.
  //
  // Non-Latin scripts are removed outright — Han, Arabic, Greek, Cyrillic, Kana, Hebrew and
  // Devanagari, none of which the laser can render. The warehouse was taking 1-2
  // unfulfillable orders a day when they got through (AU332963698 "王怡涵", AU332957303
  // "Μπαμπάς", AU332959463 "きーた姫"), and the order is already paid by the time the
  // warehouse sees it, so it has to be blocked at input rather than flagged on the pick slip.
  //
  // Punctuation IS still removed, so "O'BRIEN" becomes "OBRIEN" and "ANNE-MARIE" becomes
  // "ANNEMARIE". If the engraver ever gains apostrophe/hyphen support, add them to the class
  // below rather than reworking this method.
  //
  // The `monogram_allowed_chars` theme setting still wins if present, but NOTE that nothing
  // currently populates it — product.customiser.liquid emits only banned_words,
  // gift_set_properties, checkout_bundle_image, checkout_icon_region and
  // klaviyo_back_in_stock_form_id — so the strict default below is what actually runs. A
  // custom value must be valid inside a `u`-mode character class (e.g. `\p{L}\p{N} '` — note
  // a bare `\-` is NOT valid under `u`); anything malformed falls back to the default. A
  // custom class opts out of ASCII folding and is NFC-normalised instead, since a setting
  // that deliberately re-allows accents would be undone by folding them away.
  // Emojis are NOT handled here — they are a separate picker with its own limit.
  _cleanMono(raw) {
    const custom = this._data.settings && this._data.settings.monogram_allowed_chars;
    const useCustom = typeof custom === 'string' && !!custom.trim();
    let re;
    try {
      re = new RegExp('[^' + (useCustom ? custom.trim() : 'A-Za-z0-9 ') + ']', 'gu');
    } catch {
      re = /[^A-Za-z0-9 ]/gu;    // a malformed setting must never break the input
    }
    const src = useCustom ? String(raw || '').normalize('NFC') : foldToAscii(raw);
    return src.replace(re, '');
  }

  // Filter on the way in, preserving the caret. Rewriting `el.value` would otherwise
  // jump the cursor to the end, which is very noticeable when editing mid-word.
  // This fires for typing AND paste, so pasted text is cleaned too.
  _onMonoInput(e, c, back) {
    const el = e.target;
    const clean = this._cleanMono(el.value);
    if (clean !== el.value) {
      const removed = el.value.length - clean.length;
      const pos = Math.max(0, (el.selectionStart || 0) - removed);
      el.value = clean;
      try { el.setSelectionRange(pos, pos); } catch { /* not all inputs support it */ }
    }
    this._setMono(c, back ? 'backText' : 'text', clean);
  }

  _setMono(c, key, val) {
    const cur = this._mono[c.name] || {};
    this._mono = { ...this._mono, [c.name]: { ...cur, [key]: val } };
  }
  _toggleEmoji(c, code, limit, back) {
    const key = back ? 'backEmojis' : 'emojis';
    const cur = this._mono[c.name] || {};
    let list = (cur[key] || []).slice();
    const i = list.indexOf(code);
    if (i >= 0) list.splice(i, 1);            // clicking the selected chip -> deselect
    else if (limit === 1) list = [code];      // single-select -> replace (so a new pick switches)
    else if (!limit || list.length < limit) list.push(code);
    else return;                              // multi-select at limit -> block
    this._mono = { ...this._mono, [c.name]: { ...cur, [key]: list } };
  }

  _monoSection(c) {
    const src = this._productFor(c) || {};
    const f = src.monogramming, b = src.monogramming_back;
    const hasFront = f && f.product_id, hasBack = b && b.product_id;
    if (!hasFront && !hasBack) return '';
    const ms = this._monoStrings();
    const priceCents = Number((hasFront ? f.price : b.price) || 0);
    const badge = priceCents ? html`<span class="badge">+${this._money(priceCents)}</span>` : '';
    const label = html`${tstr(ms.title, 'Monogramming')}${badge}`;
    const body = html`
      ${hasFront ? this._monoSide(c, f, false, ms) : ''}
      ${hasBack ? html`<div class="mono-back-wrap">${this._monoSide(c, b, true, ms)}</div>` : ''}
      <p class="mono-note">${tstr(ms.render_disclaimer, 'The digital render is for illustrative purposes only. The monogram may appear slightly different in real life and may be located on the side opposite to the frank green logo and/or opposite any printed graphics.')}</p>
      ${ms.disclaimer ? html`<p class="mono-disc">${decodeEntities(ms.disclaimer)}</p>` : ''}`;
    return this._acc(c.name + ':mono', label, '', body, false);
  }

  _monoSide(c, m, back, ms) {
    const st = this._mono[c.name] || {};
    const text = (back ? st.backText : st.text) || '';
    const placements = m.availablePlacements || [];
    const limit = Number(m.character_limit) || 12;
    const banned = this._banned(text);
    const tooLong = !banned && text.length > limit;
    return html`
      <div class="mono">
        ${back ? html`<div class="mono-h">${tstr(ms.back_title, 'Back monogram')}</div>` : ''}
        <label class="mono-l">${tstr(ms.input_label, 'Your text')}</label>
        <input class="mono-in ${banned || tooLong ? 'bad' : ''}" type="text" maxlength=${limit}
          .value=${text} placeholder=${tstr(ms.placeholder, 'Type here')}
          @input=${(e) => this._onMonoInput(e, c, back)}>
        ${banned ? html`<div class="mono-warn">${tstr(ms.banned_word_message, 'Sorry, that can’t be personalised.')}</div>` : ''}
        ${tooLong ? html`<div class="mono-warn">${tstr(ms.character_limit_message, 'Up to [limit] characters on this product.').replace('[limit]', limit)}</div>` : ''}
        ${!back && placements.length > 1 ? html`
          <div class="mono-pl"><span class="mono-l">${tstr(ms.placement_label, 'Placement')}</span>
            <div class="pills">${placements.map((pl) => html`<button
              class="pill ${(st.placement || m.placement) === pl ? 'on' : ''}" aria-pressed=${(st.placement || m.placement) === pl ? 'true' : 'false'}
              @click=${() => this._setMono(c, 'placement', pl)}>${pl}</button>`)}</div>
          </div>` : ''}
        ${(m.emojis || []).length ? this._emojiPicker(c, m, back, ms) : ''}
      </div>`;
  }

  _emojiPicker(c, m, back, ms) {
    const st = this._mono[c.name] || {};
    const sel = (back ? st.backEmojis : st.emojis) || [];
    const limit = m.emoji_limit || 0;
    return html`
      <div class="mono-em">
        <span class="mono-l">${tstr(ms.emojis_label, 'Emoji')}${limit ? html` <span class="mono-count">${sel.length}/${limit}</span>` : ''}</span>
        <div class="emojis">
          ${(m.emojis || []).map((e) => {
            const on = sel.includes(e.code);
            const svg = /^\s*<svg/.test(e.svg_code || '') ? e.svg_code : '';
            return html`<button class="emoji ${on ? 'on' : ''}" aria-pressed=${on ? 'true' : 'false'} title="${e.name}"
              @click=${() => this._toggleEmoji(c, e.code, limit, back)}>
              ${svg ? html`<img src="${svgUri(svg)}" alt="${e.name}">` : e.name}</button>`;
          })}
        </div>
      </div>`;
  }

  // Extras live on the focused product for a plain product, but at the SET level for a gift set (mirrors
  // AU, where add-ons belong to the gift set as a whole, not an individual item).
  _extrasStore() { return this._isGiftSet ? this._giftExtras : this._extras; }
  _setExtrasStore(v) { if (this._isGiftSet) this._giftExtras = v; else this._extras = v; }
  _addOns() { return (this._isGiftSet ? (this._giftSet.addOns || []) : (this._data.add_ons || [])); }

  // #24: a selected COMPOSITE add-on (e.g. an extra Flip Straw Lid) is drawn onto the stage as a live overlay
  // that recolours with its swatches — mirroring the live site. Positioned by the AU `--is-add-on` container
  // rules ported into fg-customiser-layout.css, keyed by the add-on product's handle. Single product only
  // (gift-set extras are set-level, not tied to one item's stage).
  _extraStageGroups() {
    if (this._isGiftSet || !this._data) return [];
    const store = this._extras || {};
    const out = [];
    (this._data.add_ons || []).forEach((a) => {
      const sel = store[a.id]; if (!sel) return;
      const ips = innerProducts(a); if (!ips) return;
      const layers = [];
      ips.forEach((ip) => {
        if (this._partRole(ip) === 'straw') return;   // straw is never drawn in the customiser
        const v = (ip.variants || []).find((x) => x.id === (sel.inner || {})[ip.id]);
        const src = v && (v.customiser_image || v.featured_image);
        if (src) layers.push({ src, role: this._partRole(ip) });
      });
      if (layers.length) out.push({ handle: a.handle, layers });
    });
    return out;
  }

  _extrasSection() {
    const addons = this._addOns();
    if (!addons.length) return '';
    const body = html`<div class="extras">${addons.map((a) => this._extraRow(a))}</div>`;
    return this._acc('extras', tstr(this._data.strings && this._data.strings.add_ons_title, 'Extras'), '', body, false);
  }

  _extraRow(a) {
    const store = this._extrasStore();
    const sel = store[a.id];
    const on = !!sel;
    const ips = innerProducts(a);
    const variants = a.variants || [];
    return html`
      <div class="extra ${on ? 'on' : ''}">
        <div class="extra-h">
          ${a.svg_icon && /^\s*<svg/.test(a.svg_icon) ? html`<img class="extra-ico" src="${svgUri(a.svg_icon)}" alt="">` : ''}
          <span class="extra-t">${a.title}</span>
          ${Number(a.price || 0) ? html`<span class="extra-p">+${this._money(Number(a.price))}</span>` : ''}
          <button class="extra-add ${on ? 'on' : ''}" @click=${() => this._toggleExtra(a)}>${on ? 'Remove' : 'Add'}</button>
        </div>
        ${a.tooltip ? html`<p class="extra-tip">${a.tooltip}</p>` : ''}
        ${on && ips ? ips.map((ip) => {
          // Composite extra (e.g. extra lid): a colour row per inner part that offers a choice.
          const avail = (ip.variants || []).filter((v) => v.available);
          if (avail.length < 2) return '';
          const chosen = (sel.inner || {})[ip.id];
          return html`<div class="extra-colour">
            <span class="mono-l">${this._innerLabel(ip)}</span>
            <div class="swatches">${this._orderVariants(avail, chosen).map((v) => {
              const sw = v.colour_swatch || {}; const style = sw.image ? `background-image:url(${sw.image})` : `background-color:${sw.color || '#ccc'}`;
              return html`<button class="sw ${chosen === v.id ? 'on' : ''}" aria-pressed=${chosen === v.id ? 'true' : 'false'} style="${style}" aria-label="${sw.name || v.title}" @click=${() => this._setExtraInner(a, ip, v)}>${this._swTip(sw.name || v.title)}</button>`;
            })}</div>
          </div>`;
        }) : ''}
        ${on && !ips && variants.length > 1 ? html`
          <div class="swatches">${this._orderVariants(variants, (sel && typeof sel === 'object' ? sel.variantId : sel)).map((v) => {
            const sw = v.colour_swatch || {}; const vo = (sel && typeof sel === 'object' ? sel.variantId : sel) === v.id;
            const style = sw.image ? `background-image:url(${sw.image})` : `background-color:${sw.color || '#ccc'}`;
            return html`<button class="sw ${vo ? 'on' : ''}" aria-pressed=${vo ? 'true' : 'false'} style="${style}" aria-label="${sw.name || v.title}"
              ?disabled=${!v.available} @click=${() => this._setExtraVariant(a, v)}>${this._swTip(sw.name || v.title)}</button>`;
          })}</div>` : ''}
      </div>`;
  }

  _toggleExtra(a) {
    const e = { ...this._extrasStore() };
    if (e[a.id]) { delete e[a.id]; }
    else {
      const ips = innerProducts(a);
      if (ips) { const inner = {}; ips.forEach((ip) => { const v = this._firstAvail(ip); if (v) inner[ip.id] = v.id; }); e[a.id] = { inner }; }
      else { const v = (a.variants || []).find((x) => x.available) || (a.variants || [])[0]; if (v) e[a.id] = { variantId: v.id }; }
    }
    this._setExtrasStore(e);
  }
  _setExtraVariant(a, v) { if (!v.available) return; this._setExtrasStore({ ...this._extrasStore(), [a.id]: { variantId: v.id } }); }
  _setExtraInner(a, ip, v) {
    if (!v.available) return;
    const store = this._extrasStore();
    const cur = store[a.id] || { inner: {} };
    this._setExtrasStore({ ...store, [a.id]: { ...cur, inner: { ...(cur.inner || {}), [ip.id]: v.id } } });
  }

  _acc(key, label, val, body, def) {
    // Namespace by focused item so two gift-set items with same-named components don't share open state.
    const k = this._focus + '|' + key;
    const open = this._isOpen(k, def);
    return html`
      <div class="acc ${open ? 'open' : ''}">
        <button class="acc-h" @click=${() => this._toggle(k, def)}>
          <span class="acc-label">${label}</span>
          ${val ? html`<span class="acc-val">${val}</span>` : ''}
          <span class="acc-ico">${open ? '–' : '+'}</span>
        </button>
        ${open ? html`<div class="acc-body">${body}</div>` : ''}
      </div>`;
  }

  _typeSection(c, label) {
    const p = this._productFor(c);
    const body = html`
      <div class="types">
        ${(c.products || []).map((tp) => {
          const on = tp.id === p.id;
          const avail = (tp.variants || []).some((v) => v.available) || !!innerProducts(tp);
          const tip = decodeEntities(tp.tooltip || '');
          return html`<button class="type ${on ? 'on' : ''}" aria-pressed=${on ? 'true' : 'false'} ?disabled=${!avail} @click=${() => this._pickProduct(c, tp)}>
            ${this._surBadge(this._lidBaseSurcharge(tp), 'type-sur')}
            ${this._icon(this._partClass(c, tp))}<span>${this._lidName(tp)}</span>
            ${tip ? html`<span class="type-info" aria-label=${tip} @click=${(e) => e.stopPropagation()}>?<span class="type-tip">${tip}</span></span>` : ''}
          </button>`;
        })}
      </div>`;
    return this._acc(c.name + ':type', label, this._lidName(p), body, true);
  }

  // Hover name bubble shown above a colour swatch (styled by .sw-tip). Empty when there's no name.
  _swTip(name) { return name ? html`<span class="sw-tip">${name}</span>` : ''; }

  _colourSection(c, label) {
    const p = this._productFor(c), cur = this._variantFor(c);
    const val = (cur && cur.colour_swatch && cur.colour_swatch.name) || (cur && cur.title) || '';
    const floor = this._floorSurcharge(p?.variants);
    const body = html`
      <div class="swatches">
        ${this._orderVariants(p?.variants, cur && cur.id).map((v) => {
          const sw = v.colour_swatch || {};
          const on = cur && v.id === cur.id;
          const style = sw.image ? `background-image:url(${sw.image})` : `background-color:${sw.color || '#ccc'}`;
          return html`<button class="sw ${on ? 'on' : ''}" aria-pressed=${on ? 'true' : 'false'} style="${style}" ?disabled=${!v.available}
            aria-label="${sw.name || v.title}" @click=${() => this._pick(c, p, v)}>${this._swTip(sw.name || v.title)}${this._surBadge(Number(v.surcharge_price || 0) - floor, 'sw-sur')}</button>`;
        })}
      </div>`;
    return this._acc(c.name + ':colour', label, val, body, false);
  }

  // Colour row for one inner part of a composite lid (Lid/Strap/Button Colour).
  _innerColourSection(c, ip) {
    const cur = this._innerVar(c, ip);
    const val = (cur && cur.colour_swatch && cur.colour_swatch.name) || (cur && cur.title) || '';
    const floor = this._floorSurcharge(ip.variants);
    const body = html`
      <div class="swatches">
        ${this._orderVariants(ip.variants, cur && cur.id).map((v) => {
          const sw = v.colour_swatch || {};
          const on = cur && v.id === cur.id;
          const style = sw.image ? `background-image:url(${sw.image})` : `background-color:${sw.color || '#ccc'}`;
          return html`<button class="sw ${on ? 'on' : ''}" aria-pressed=${on ? 'true' : 'false'} style="${style}" ?disabled=${!v.available}
            aria-label="${sw.name || v.title}" @click=${() => this._pickInner(c, ip, v)}>${this._swTip(sw.name || v.title)}${this._surBadge(Number(v.surcharge_price || 0) - floor, 'sw-sur')}</button>`;
        })}
      </div>`;
    return this._acc(c.name + ':inner:' + ip.id, this._innerLabel(ip), val, body, false);
  }

  // Colour row for one PART of an assembly (standalone lid): Lid / Strap / Straw Colour.
  _assemblyPartColour(c, p) {
    const parts = (this._sel[c.name] || {}).parts || {};
    const cur = (p.variants || []).find((v) => v.id === parts[p.id]);
    const val = (cur && cur.colour_swatch && cur.colour_swatch.name) || (cur && cur.title) || '';
    const floor = this._floorSurcharge(p.variants);
    const body = html`
      <div class="swatches">
        ${this._orderVariants(p.variants, cur && cur.id).map((v) => {
          const sw = v.colour_swatch || {};
          const on = cur && v.id === cur.id;
          const style = sw.image ? `background-image:url(${sw.image})` : `background-color:${sw.color || '#ccc'}`;
          return html`<button class="sw ${on ? 'on' : ''}" aria-pressed=${on ? 'true' : 'false'} style="${style}" ?disabled=${!v.available}
            aria-label="${sw.name || v.title}" @click=${() => this._pickAssemblyPart(c, p, v)}>${this._swTip(sw.name || v.title)}${this._surBadge(Number(v.surcharge_price || 0) - floor, 'sw-sur')}</button>`;
        })}
      </div>`;
    return this._acc(c.name + ':asm:' + p.id, this._innerLabel(p), val, body, false);
  }

  // Randomise COLOURS ONLY, keeping the current product/lid-type per component (#19). Randomising the lid
  // TYPE/strap product too pulled in a new set of part images each click — slow + visually "glitchy". Keeping
  // the current selection and only re-rolling each part's colour means no new-product image loads.
  _randomise() {
    const pickRand = (vs) => { const a = (vs || []).filter((v) => v.available); return a.length ? a[Math.floor(Math.random() * a.length)] : (vs || [])[0]; };
    const sel = { ...this._sel };
    (this._data.components || []).forEach((c) => {
      const cur = sel[c.name];
      if (cur && cur.assembly) { const parts = { ...(cur.parts || {}) }; (c.products || []).forEach((p) => { const v = pickRand(p.variants); if (v) parts[p.id] = v.id; }); sel[c.name] = { assembly: true, parts }; return; }
      const p = (c.products || []).find((x) => x.id === cur?.productId) || this._productFor(c);   // keep current product/type
      if (!p) return;
      const ips = innerProducts(p);
      if (ips) { const inner = { ...(cur?.inner || {}) }; ips.forEach((ip) => { const v = pickRand(ip.variants); if (v) inner[ip.id] = v.id; }); sel[c.name] = { ...cur, productId: p.id, inner }; }
      else { const v = pickRand(p.variants); sel[c.name] = { ...cur, productId: p.id, variantId: v ? v.id : cur?.variantId }; }
    });
    this._sel = sel;
  }

  // The X in the panel header asks the section wrapper to restore the normal buy block.
  _close() { this.dispatchEvent(new CustomEvent('fg-close', { bubbles: true, composed: true })); }

  // a compact [label, value] build summary for the cart line (fg-cart-image.js renders it under the title).
  // Single product: rows for the one product. Gift set: rows for every item, prefixed with the item title.
  _summary() {
    if (!this._isGiftSet) return this._summaryForItem(this._it, '');
    const out = [];
    this._items.forEach((it, i) => { this._summaryForItem(it, this._giftItemLabel(it, i)).forEach((row) => out.push(row)); });
    if (this._giftSet.addOns) resolveExtras({ add_ons: this._giftSet.addOns }, this._giftExtras).forEach((e) => out.push(['Extra', e.title + (e.variantTitle && e.variantTitle !== e.title ? ' — ' + e.variantTitle : '')]));
    return out;
  }
  _summaryForItem(it, prefix) {
    if (!it) return [];
    const out = [];
    const pfx = prefix ? prefix + ': ' : '';
    ((it.data && it.data.components) || []).forEach((c) => {
      const s = it.sel[c.name];
      if (s && s.assembly) {
        (c.products || []).forEach((p) => { const v = (p.variants || []).find((x) => x.id === (s.parts || {})[p.id]); if (v && (p.variants || []).filter((x) => x.available).length > 1) out.push([pfx + this._innerLabel(p), (v.colour_swatch && v.colour_swatch.name) || v.title || '']); });
        return;
      }
      const p = (c.products || []).find((x) => x.id === (s && s.productId)) || (c.products || [])[0];
      const label = c.display_title || c.name || 'Part';
      if ((c.products || []).length > 1 && p) out.push([pfx + label + ' type', this._lidName(p)]);
      const ips = innerProducts(p);
      if (ips) {
        ips.forEach((ip) => {
          const v = innerVariant(ip, it.sel, c.name);
          if (v && (ip.variants || []).filter((x) => x.available).length > 1) out.push([pfx + this._innerLabel(ip), (v.colour_swatch && v.colour_swatch.name) || v.title || '']);
        });
      } else {
        const v = (p && (p.variants || []).find((x) => x.id === (s && s.variantId))) || null;
        if (v) out.push([pfx + label + ' colour', (v.colour_swatch && v.colour_swatch.name) || v.title || '']);
      }
    });
    resolveMono(it.data, it.sel, it.mono).forEach((m) => {
      Object.keys(m.props).forEach((k) => { if (!/^_/.test(k) && k !== 'Bundle #' && k !== 'Item') out.push([pfx + k, m.props[k]]); });
    });
    resolveExtras(it.data, it.extras).forEach((e) => out.push([pfx + 'Extra', e.title + (e.variantTitle && e.variantTitle !== e.title ? ' — ' + e.variantTitle : '')]));
    return out;
  }

  _loadImg(src) { return new Promise((res) => { const im = new Image(); im.crossOrigin = 'anonymous'; im.onload = () => res(im); im.onerror = () => res(null); im.src = src; }); }

  // Cart thumbnail. Single product -> the accurate DOM-transform composite of the focused stage. Gift set
  // -> every item composited into its own grid cell, using each item's on-screen gift-set thumbnail to read
  // the resolved per-part transforms (so cart items match the stage instead of a flat contain-fit stack).
  async _capture() {
    if (this._isGiftSet) return this._captureGift();
    return this._captureFocused();
  }

  // Composite `layers` (from _layerParts/_layerPartsFor) into the rect (ox,oy,cw,ch) on ctx, applying each
  // layer's RESOLVED CSS transform read from its matching DOM elem so the output matches the on-screen
  // stack. `elems` must be in the same order as `layers`; an empty/short list falls back to identity
  // (a clean contain-fit) for any missing elem. Draws in z-index order (stable tiebreak on index) so the
  // base paints under the lid regardless of array order. Returns the number of layers drawn.
  async _drawLayers(ctx, layers, elems, ox, oy, cw, ch) {
    const zOf = (el) => { if (!el) return 0; const z = parseInt(getComputedStyle(el).zIndex, 10); return Number.isFinite(z) ? z : 0; };
    const order = layers.map((_, i) => i).sort((a, b) => (zOf(elems[a]) - zOf(elems[b])) || (a - b));
    let drew = 0;
    for (const i of order) {
      const im = await this._loadImg(layers[i].src); if (!im) continue;
      let s = 1, txFrac = 0, tyFrac = 0;
      const el = elems[i];
      if (el) {
        try {
          // offsetWidth/Height = LAYOUT box (ignores transforms). m.e/m.f are the resolved translate in px
          // (= scale * translate% * layoutSize); dividing by layout size yields scale*translateFraction,
          // which is resolution-independent so it maps cleanly onto any cell size (stage or thumbnail).
          const cs = getComputedStyle(el);
          const bw = el.offsetWidth, bh = el.offsetHeight;
          const m = new DOMMatrixReadOnly(cs.transform && cs.transform !== 'none' ? cs.transform : '');
          s = m.a || 1;
          if (bw) txFrac = m.e / bw;
          if (bh) tyFrac = m.f / bh;
        } catch (_) { /* fall back to identity */ }
      }
      const r = Math.min(cw / im.width, ch / im.height), iw = im.width * r, ih = im.height * r;
      const dw = iw * s, dh = ih * s;
      const cx = ox + cw / 2 + txFrac * cw, cy = oy + ch / 2 + tyFrac * ch;
      ctx.drawImage(im, cx - dw / 2, cy - dh / 2, dw, dh);
      drew++;
    }
    return drew;
  }

  async _captureGift() {
    try {
      // Pair each item's layers with its on-screen gift-set thumbnail elems (same order as _layerPartsFor),
      // so we can read the resolved transforms and lay each item in correctly rather than flat-stacking.
      const itemEls = [...this.renderRoot.querySelectorAll('.giftset-items .giftset-item')];
      const items = this._items
        .map((it, i) => ({ layers: this._layerPartsFor(it), elems: itemEls[i] ? [...itemEls[i].querySelectorAll('.fg-cust__dynamic-image--elem')] : [] }))
        .filter((x) => x.layers.length);
      if (!items.length) return null;
      const n = items.length;
      const cols = n <= 2 ? n : (n <= 4 ? 2 : 3);         // 2 items -> side by side
      const rows = Math.ceil(n / cols);
      const CW = 300, CH = 375;                            // 4:5 cells (matches the elem aspect ratio)
      const cv = document.createElement('canvas'); cv.width = cols * CW; cv.height = rows * CH;
      const ctx = cv.getContext('2d');
      let drew = 0;
      for (let idx = 0; idx < items.length; idx++) {
        const ox = (idx % cols) * CW, oy = Math.floor(idx / cols) * CH;
        drew += await this._drawLayers(ctx, items[idx].layers, items[idx].elems, ox, oy, CW, CH);
      }
      return drew ? cv.toDataURL('image/png') : null;
    } catch (e) { console.warn('[FG] gift thumbnail capture failed', e); return null; }
  }

  async _captureFocused() {
    try {
      const parts = this._layerParts();
      if (!parts.length) return null;
      // Scope to the stage so a gift set's thumbnail elems (also .fg-cust__dynamic-image--elem) don't leak in.
      const elems = [...this.renderRoot.querySelectorAll('.stage .fg-cust__dynamic-image--elem')];
      const W = 600, H = 750, cv = document.createElement('canvas'); cv.width = W; cv.height = H; // 4:5
      const drew = await this._drawLayers(cv.getContext('2d'), parts, elems, 0, 0, W, H);
      return drew ? cv.toDataURL('image/png') : null;
    } catch (e) { console.warn('[FG] thumbnail capture failed', e); return null; }
  }

  // The customiser JSON is rendered (and edge-cached) with availability from page-render time, so a
  // colour can sell out between opening the customiser and hitting Add to cart (the client hit exactly
  // this). Before submitting, re-fetch the live customiser data (cache-busted), sync every swatch's
  // availability into the UI, and return the names of any selected variants that are now out of stock.
  // Fails OPEN (returns []) if the re-check can't run, so a network blip never blocks a legit sale —
  // the atomic /cart/add.js remains the final backstop.
  async _recheckStock() {
    const map = new Map();
    try {
      const urls = new Set();
      if (this._isGiftSet) {
        this._items.forEach((it) => { const h = it.data && it.data.product && it.data.product.handle; if (h) urls.add(this._memberUrl(h)); });
      } else {
        const base = (this.productUrl || location.pathname).split('?')[0];
        urls.add(base + '?view=customiser');
      }
      await Promise.all([...urls].map(async (u) => {
        const bust = u + (u.includes('?') ? '&' : '?') + '_=' + Date.now();
        try { collectVariantAvail(await this._fetchJson(bust), map); } catch (e) { /* skip this url */ }
      }));
    } catch (e) { return []; }
    if (!map.size) return [];   // couldn't verify — don't block the sale
    this._items.forEach((it) => applyVariantAvail(it.data, map));
    this.requestUpdate();
    const bad = new Set();
    const check = (id, fallback) => { const r = map.get(String(id)); if (r && r.available === false) bad.add(r.name || fallback || 'a selected colour'); };
    this._items.forEach((it) => {
      resolve(it.data, it.sel).forEach((p) => check(p.id));
      resolveExtras(it.data, it.extras).forEach((e) => check(e.id, e.variantTitle));
    });
    if (this._isGiftSet && this._giftSet.addOns) {
      resolveExtras({ add_ons: this._giftSet.addOns }, this._giftExtras).forEach((e) => check(e.id, e.variantTitle));
    }
    return [...bad];
  }

  async _addToCart() {
    if (this._adding) return;
    // Belt and braces for the button gate: nothing may post a banned or over-length monogram.
    const monoBad = this._firstMonoIssue();
    if (monoBad) { this._focusMonoIssue(monoBad); return; }
    this._adding = true;
    try {
      const soldOut = await this._recheckStock();
      if (soldOut.length) {
        this._adding = false;
        alert('Sorry — ' + (soldOut.length > 1 ? 'these colours just sold out' : 'this colour just sold out') + ' while you were customising: '
          + soldOut.join(', ') + '.\n\nThe swatches have been updated — please choose another to continue.');
        return;
      }
      const thumbnail = await this._capture();
      if (this._isGiftSet) await this._addGiftToCart(thumbnail);
      else await addToCart({ data: this._data, sel: this._sel, mono: this._mono, extras: this._extras, cartRoot: this.cartRoot, afterAdd: 'drawer', thumbnail, summary: this._summary() });
    } catch (e) {
      console.error('[FG] add to cart failed', e);
      alert('Add to cart failed: ' + e.message);
    } finally {
      this._adding = false;
    }
  }

  // Add the whole gift set as one WICK bundle parented to the set product's variant at the fixed total.
  async _addGiftToCart(thumbnail) {
    const root = cartRootOf(this.cartRoot);
    const bundleNum = await scanBundleNum(root);
    const bundleId = 'wick_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    const setExtras = this._giftSet.addOns ? resolveExtras({ add_ons: this._giftSet.addOns }, this._giftExtras) : [];
    const built = buildGiftItems(this._giftSet, this._items, bundleId, bundleNum, this._totalCents(), setExtras);
    if (!built.items.length) throw new Error('Nothing to add — no part variants resolved');
    return submitItems({ root, afterAdd: 'drawer', items: built.items, bundleId, name: built.name, thumbnail, summary: this._summary() });
  }
}

customElements.define('fg-customiser', FgCustomiser);
